// src/components/AIActivityQuestionsModal.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Sparkles,
  SlidersHorizontal,
  Info,
  FolderOpen,
  Trash2,
  FileText,
  FileUp,
  Loader2,
} from "lucide-react";
import authService from "../services/authService";

const STYLE_KEYS = ["conceptual", "caso_practico", "comparacion", "trampa_comun"];
const STYLE_LABELS = {
  conceptual: "Conceptual",
  caso_practico: "Caso práctico",
  comparacion: "Comparación",
  trampa_comun: "Trampa común",
};

const clampInt = (v, min = 0, max = 9999) => {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
};

const trimOrNull = (v) => {
  const s = String(v ?? "").trim();
  return s ? s : null;
};

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const tryParseJson = (v) => {
  if (v == null) return v;
  if (typeof v !== "string") return v;
  const s = v.trim();
  if (!s) return v;
  try {
    return JSON.parse(s);
  } catch {
    return v;
  }
};

const getParsed = (q) => {
  // En BD a veces vienen como JSON string
  const opciones = tryParseJson(q?.opciones);
  const respuesta_correcta = tryParseJson(q?.respuesta_correcta);
  return { opciones, respuesta_correcta };
};

const prettyTipo = (q) => {
  // Priorizamos el enum real de plataforma
  const raw = q?.tipo_pregunta ?? q?.tipo;
  const t = String(raw || "")
    .toLowerCase()
    .trim();
  const { respuesta_correcta } = getParsed(q);

  if (t === "seleccion_multiple") {
    const correctas = Array.isArray(respuesta_correcta?.correctas)
      ? respuesta_correcta.correctas
      : [];
    return correctas.length <= 1 ? "UNICA" : "MULTIPLE";
  }
  if (t === "verdadero_falso") return "VF";
  if (t === "completar") return "COMPLETAR";
  if (t === "respuesta_corta") return "CORTA";

  // Compatibilidad con valores del prompt u otros
  if (
    [
      "unica",
      "opcion_unica",
      "seleccion_unica",
      "single",
      "single_choice",
    ].includes(t)
  )
    return "UNICA";
  if (["multiple", "opcion_multiple", "multiple_choice", "multi"].includes(t))
    return "MULTIPLE";
  if (["vf", "v/f", "verdadero_falso", "truefalse", "true_false"].includes(t))
    return "VF";
  if (
    [
      "completar",
      "completar_espacios",
      "rellenar",
      "fill_blank",
      "fill_in_the_blank",
    ].includes(t)
  )
    return "COMPLETAR";
  return raw ? String(raw).toUpperCase() : "—";
};

const prettyBloom = (raw) => {
  const b = String(raw || "")
    .toLowerCase()
    .trim();
  if (!b) return null;
  // Mantén el texto igual que en uploader.html (MAYÚSCULAS)
  return b.toUpperCase();
};

const normalizeOpciones = (q) => {
  const { opciones: opcionesParsed, respuesta_correcta } = getParsed(q);
  const correctasIds = Array.isArray(respuesta_correcta?.correctas)
    ? respuesta_correcta.correctas
    : null;

  // 1) Caso plataforma (aiMappers): opciones = { opciones: [{id,texto}, ...] }
  const fromPlatform = Array.isArray(opcionesParsed?.opciones)
    ? opcionesParsed.opciones
    : null;
  if (fromPlatform) {
    return fromPlatform.map((o, i) => {
      const id = Number(o?.id ?? i + 1);
      const etiqueta = LETTERS[i] || String(i + 1);
      const texto = String(o?.texto ?? "");
      const correcta = Array.isArray(correctasIds)
        ? correctasIds.includes(id)
        : false;
      return { id, etiqueta, texto, correcta };
    });
  }

  // 2) Caso array directo (uploader / otras fuentes)
  if (Array.isArray(opcionesParsed)) {
    return opcionesParsed.map((o, i) => {
      const etiqueta =
        String(o?.etiqueta || o?.label || "").trim() ||
        LETTERS[i] ||
        String(i + 1);
      const texto = String(o?.texto ?? o?.text ?? o?.value ?? "");
      const correcta = !!(o?.correcta ?? o?.is_correct ?? o?.correct);
      return { ...o, etiqueta, texto, correcta };
    });
  }

  // 3) Caso objeto tipo {A:"...",B:"..."}
  if (opcionesParsed && typeof opcionesParsed === "object") {
    const keys = Object.keys(opcionesParsed);
    // evita agarrar estructuras especiales de completar (texto_con_espacios)
    const looksLikeABCD = keys.some((k) => /^[A-Z]$/i.test(k));
    if (looksLikeABCD) {
      return keys
        .filter((k) => /^[A-Z]$/i.test(k))
        .sort()
        .map((k, i) => {
          const etiqueta = k.toUpperCase();
          const texto = String(opcionesParsed[k] ?? "");
          // Si no tenemos ids, intentamos por letras en respuesta_correcta
          const correcta = false;
          return { id: i + 1, etiqueta, texto, correcta };
        });
    }
  }

  // 4) VF: fabricamos opciones si no vienen
  if (String(q?.tipo_pregunta || "").toLowerCase() === "verdadero_falso") {
    const rc = String(respuesta_correcta?.respuesta ?? "").toLowerCase();
    const isV = rc.includes("verd") || rc === "v";
    const isF = rc.includes("fals") || rc === "f";
    return [
      { id: 1, etiqueta: "A", texto: "Verdadero", correcta: isV && !isF },
      { id: 2, etiqueta: "B", texto: "Falso", correcta: isF && !isV },
    ];
  }

  return [];
};

const formatRespuestaCorrecta = (q, opcionesNorm) => {
  const { respuesta_correcta } = getParsed(q);
  const rc =
    respuesta_correcta ?? q?.respuesta ?? q?.correcta ?? q?.answer ?? null;

  // 1) Si viene como array de opciones/objetos
  if (Array.isArray(rc)) {
    const labels = rc
      .map((x) =>
        typeof x === "string"
          ? x
          : x?.etiqueta || x?.label || x?.texto || x?.text || "",
      )
      .map((s) => String(s || "").trim())
      .filter(Boolean);
    if (labels.length) return labels.join(", ");
  }

  // 2) Si viene como objeto (lo típico del [object Object])
  if (rc && typeof rc === "object") {
    const label = String(rc?.etiqueta || rc?.label || "").trim();
    if (label) return label;
    const asText = String(rc?.texto || rc?.text || "").trim();
    if (asText) {
      // Intentar mapear a una letra si coincide con una opción
      const idx = (opcionesNorm || []).findIndex(
        (o) => String(o.texto).trim() === asText,
      );
      if (idx >= 0)
        return opcionesNorm[idx].etiqueta || LETTERS[idx] || String(idx + 1);
      return asText;
    }
  }

  // 3) Si viene como string/number/bool
  if (rc != null && typeof rc !== "object") {
    const s = String(rc).trim();
    if (s) return s;
  }

  // 4) Fallback: derivar de opciones marcadas como correctas
  const corrects = (opcionesNorm || []).filter((o) => o.correcta);
  if (corrects.length) return corrects.map((o) => o.etiqueta).join(", ");

  return null;
};

function distributeEvenly(total, enabledKeys) {
  const keys = enabledKeys.filter(Boolean);
  const out = { unica: 0, multiple: 0, vf: 0, completar: 0 };
  if (!keys.length) return out;
  const base = Math.floor(total / keys.length);
  let rem = total - base * keys.length;
  keys.forEach((k) => (out[k] = base));
  for (let i = 0; i < keys.length && rem > 0; i++) {
    out[keys[i]] += 1;
    rem--;
  }
  return out;
}

function sumObj(obj) {
  return Object.values(obj || {}).reduce((a, b) => a + (Number(b) || 0), 0);
}

function getThemeAwareHintColors() {
  if (typeof document === "undefined") {
    return { ok: "#166534", warn: "#92400e", neutral: "#374151" };
  }
  const isLight =
    document.documentElement.getAttribute("data-theme") === "light";
  return isLight
    ? { ok: "#166534", warn: "#92400e", neutral: "#374151" }
    : { ok: "#15803d", warn: "#b45309", neutral: "#475569" };
}

function normalizeQuestionTypeCounts(total, enabledKeys) {
  const safeTotal = Math.max(0, parseInt(total || "0", 10) || 0);
  const keys = (enabledKeys || []).filter(Boolean);
  const out = { unica: 0, multiple: 0, vf: 0, completar: 0 };
  if (!safeTotal || !keys.length) return out;
  const base = Math.floor(safeTotal / keys.length);
  let rest = safeTotal - base * keys.length;
  keys.forEach((key, index) => {
    out[key] = base + (index < rest ? 1 : 0);
  });
  return out;
}

function distributeCounts(total, keys, seed = {}) {
  const safeTotal = Math.max(0, parseInt(total || "0", 10) || 0);
  const enabled = (keys || []).filter(Boolean);
  const out = {};
  enabled.forEach((key) => {
    out[key] = 0;
  });
  if (!safeTotal || !enabled.length) return { ...seed, ...out };
  const base = Math.floor(safeTotal / enabled.length);
  let rest = safeTotal - base * enabled.length;
  enabled.forEach((key) => {
    out[key] = base + (rest > 0 ? 1 : 0);
    if (rest > 0) rest -= 1;
  });
  return { ...seed, ...out };
}

function clampGroupToTotal(values, changedKey, rawValue, total) {
  const safeTotal = clampInt(total, 0, 50);
  const next = { ...values };
  next[changedKey] = clampInt(rawValue, 0, safeTotal || 50);
  if (!safeTotal) return next;

  let sum = sumObj(next);
  if (sum <= safeTotal) return next;

  const overflow = sum - safeTotal;
  next[changedKey] = Math.max(0, next[changedKey] - overflow);
  sum = sumObj(next);
  if (sum <= safeTotal) return next;

  return distributeCounts(safeTotal, Object.keys(next), next);
}

const BLOOM_LEVELS = [
  "recordar",
  "comprender",
  "aplicar",
  "analizar",
  "evaluar",
  "crear",
];

function getResourceExt(resource) {
  const raw = String(
    resource?.nombre_original ||
    resource?.nombre_archivo ||
    resource?.titulo ||
    resource?.name ||
    resource?._localFile?.name ||
    "",
  ).toLowerCase();
  const match = raw.match(/\.([a-z0-9]+)$/);
  return match ? match[1] : "";
}

function canUseResourceRange(resource) {
  return ["pdf", "docx", "pptx", "txt"].includes(getResourceExt(resource));
}

const _readUInt16LE = (bytes, offset) =>
  bytes[offset] | (bytes[offset + 1] << 8);
const _readUInt32LE = (bytes, offset) =>
  (bytes[offset] |
    (bytes[offset + 1] << 8) |
    (bytes[offset + 2] << 16) |
    (bytes[offset + 3] << 24)) >>>
  0;

function parseZipEntries(bytes) {
  if (!(bytes instanceof Uint8Array) || bytes.length < 22) return [];

  let eocdOffset = -1;
  const minSearch = Math.max(0, bytes.length - 65557);
  for (let i = bytes.length - 22; i >= minSearch; i -= 1) {
    if (
      bytes[i] === 0x50 &&
      bytes[i + 1] === 0x4b &&
      bytes[i + 2] === 0x05 &&
      bytes[i + 3] === 0x06
    ) {
      eocdOffset = i;
      break;
    }
  }
  if (eocdOffset < 0) return [];

  const totalEntries = _readUInt16LE(bytes, eocdOffset + 10);
  const centralDirOffset = _readUInt32LE(bytes, eocdOffset + 16);
  const decoder = new TextDecoder("utf-8");
  const entries = [];
  let ptr = centralDirOffset;

  for (let i = 0; i < totalEntries && ptr + 46 <= bytes.length; i += 1) {
    if (
      !(
        bytes[ptr] === 0x50 &&
        bytes[ptr + 1] === 0x4b &&
        bytes[ptr + 2] === 0x01 &&
        bytes[ptr + 3] === 0x02
      )
    ) {
      break;
    }
    const compressionMethod = _readUInt16LE(bytes, ptr + 10);
    const compressedSize = _readUInt32LE(bytes, ptr + 20);
    const uncompressedSize = _readUInt32LE(bytes, ptr + 24);
    const fileNameLength = _readUInt16LE(bytes, ptr + 28);
    const extraLength = _readUInt16LE(bytes, ptr + 30);
    const commentLength = _readUInt16LE(bytes, ptr + 32);
    const localHeaderOffset = _readUInt32LE(bytes, ptr + 42);
    const nameStart = ptr + 46;
    const nameEnd = nameStart + fileNameLength;
    const name = decoder.decode(bytes.subarray(nameStart, nameEnd));

    entries.push({
      name,
      compressionMethod,
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
    });

    ptr = nameEnd + extraLength + commentLength;
  }

  return entries;
}

async function inflateZipEntry(bytes, entry) {
  if (!(bytes instanceof Uint8Array) || !entry) return null;
  const ptr = entry.localHeaderOffset;
  if (ptr + 30 > bytes.length) return null;
  if (
    !(
      bytes[ptr] === 0x50 &&
      bytes[ptr + 1] === 0x4b &&
      bytes[ptr + 2] === 0x03 &&
      bytes[ptr + 3] === 0x04
    )
  ) {
    return null;
  }

  const fileNameLength = _readUInt16LE(bytes, ptr + 26);
  const extraLength = _readUInt16LE(bytes, ptr + 28);
  const dataStart = ptr + 30 + fileNameLength + extraLength;
  const dataEnd = dataStart + entry.compressedSize;
  if (dataEnd > bytes.length) return null;

  const compressed = bytes.subarray(dataStart, dataEnd);
  if (entry.compressionMethod === 0) return compressed;
  if (
    entry.compressionMethod !== 8 ||
    typeof DecompressionStream === "undefined"
  ) {
    return null;
  }

  try {
    const ds = new DecompressionStream("deflate-raw");
    const stream = new Blob([compressed]).stream().pipeThrough(ds);
    const ab = await new Response(stream).arrayBuffer();
    return new Uint8Array(ab);
  } catch {
    return null;
  }
}

async function readZipEntryText(bytes, entryName) {
  try {
    const entries = parseZipEntries(bytes);
    const entry = entries.find((item) => item.name === entryName);
    if (!entry) return null;
    const raw = await inflateZipEntry(bytes, entry);
    if (!raw) return null;
    return new TextDecoder("utf-8").decode(raw);
  } catch {
    return null;
  }
}

async function readPdfPageCount(file) {
  if (!file || getResourceExt({ _localFile: file }) !== "pdf") {
    return { count: null, approx: false };
  }
  try {
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let raw = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      raw += String.fromCharCode.apply(
        null,
        bytes.subarray(i, Math.min(i + chunkSize, bytes.length)),
      );
    }

    const countMatches = Array.from(raw.matchAll(/\/Count\s+(\d+)/g))
      .map((m) => Number.parseInt(m[1], 10))
      .filter((n) => Number.isInteger(n) && n > 0);
    if (countMatches.length) {
      return { count: Math.max(...countMatches), approx: false };
    }

    const pageMatches = raw.match(/\/Type\s*\/Page\b/g);
    if (pageMatches?.length)
      return { count: pageMatches.length, approx: false };
  } catch {
    // no-op
  }
  return { count: null, approx: false };
}

async function readDocxPageCount(file) {
  if (!file || getResourceExt({ _localFile: file }) !== "docx") {
    return { count: null, approx: false };
  }
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const appXml = await readZipEntryText(bytes, "docProps/app.xml");
    const pagesMatch = String(appXml || "").match(/<Pages>(\d+)<\/Pages>/i);
    if (pagesMatch) {
      const count = Number.parseInt(pagesMatch[1], 10);
      if (Number.isInteger(count) && count > 0) {
        return { count, approx: false };
      }
    }

    const docXml = await readZipEntryText(bytes, "word/document.xml");
    if (docXml) {
      const breaks = (docXml.match(/w:type="page"/g) || []).length;
      const paras = (docXml.match(/<w:p\b/g) || []).length;
      const est = Math.max(1, breaks + Math.ceil(paras / 35));
      return { count: est, approx: true };
    }
  } catch {
    // no-op
  }
  return { count: null, approx: false };
}

async function readPptxPageCount(file) {
  if (!file || getResourceExt({ _localFile: file }) !== "pptx") {
    return { count: null, approx: false };
  }
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const entries = parseZipEntries(bytes);
    const slideNames = entries
      .map((item) => item.name)
      .filter((name) => /^ppt\/slides\/slide\d+\.xml$/i.test(name));
    if (slideNames.length) return { count: slideNames.length, approx: false };
  } catch {
    // no-op
  }
  return { count: null, approx: false };
}

async function readTxtPageCount(file) {
  if (!file || getResourceExt({ _localFile: file }) !== "txt") {
    return { count: null, approx: false };
  }
  try {
    const text = await file.text();
    if (!text) return { count: 1, approx: false };
    const formFeedParts = text.split(//g).filter((part) => part.trim());
    if (formFeedParts.length > 1) {
      return { count: formFeedParts.length, approx: false };
    }
    const lines = text.split(/\r?\n/).length;
    return { count: Math.max(1, Math.ceil(lines / 45)), approx: true };
  } catch {
    // no-op
  }
  return { count: null, approx: false };
}

async function readResourcePageCountFromFile(file) {
  if (!file) return { count: null, approx: false };
  const ext = getResourceExt({ _localFile: file });
  if (ext === "pdf") return readPdfPageCount(file);
  if (ext === "docx") return readDocxPageCount(file);
  if (ext === "pptx") return readPptxPageCount(file);
  if (ext === "txt") return readTxtPageCount(file);
  return { count: null, approx: false };
}

function getResourcePageCount(resource) {
  const candidates = [
    resource?.pageCount,
    resource?.page_count,
    resource?.pages,
    resource?.paginas,
    resource?.total_pages,
    resource?.num_pages,
    resource?.page_total,
    resource?.pages_total,
    resource?.paginas_total,
    resource?.total_paginas,
    resource?.cantidad_paginas,
    resource?.numero_paginas,
    resource?.n_paginas,
    resource?.meta?.pageCount,
    resource?.meta?.page_count,
    resource?.meta?.pages,
    resource?.meta?.paginas,
    resource?.meta?.total_pages,
    resource?.meta?.total_paginas,
    resource?.metadata?.pageCount,
    resource?.metadata?.page_count,
    resource?.metadata?.pages,
    resource?.metadata?.paginas,
    resource?.metadata?.total_pages,
    resource?.metadata?.total_paginas,
    resource?.slides,
    resource?.slide_count,
    resource?.total_slides,
    resource?.slides_total,
    resource?.total_diapositivas,
    resource?.cantidad_diapositivas,
    resource?.meta?.slides,
    resource?.meta?.slide_count,
    resource?.meta?.total_slides,
    resource?.metadata?.slides,
    resource?.metadata?.slide_count,
    resource?.metadata?.total_slides,
  ];
  for (const value of candidates) {
    const n = Number.parseInt(String(value ?? ""), 10);
    if (Number.isInteger(n) && n > 0) return n;
  }
  return null;
}

function getResourcePageCountApprox(resource) {
  return !!(
    resource?.pageCountApprox ||
    resource?.page_count_approx ||
    resource?.pages_approx ||
    resource?.paginas_aprox ||
    resource?.meta?.pageCountApprox ||
    resource?.meta?.page_count_approx ||
    resource?.meta?.pages_approx ||
    resource?.meta?.paginas_aprox ||
    resource?.metadata?.pageCountApprox ||
    resource?.metadata?.page_count_approx ||
    resource?.metadata?.pages_approx ||
    resource?.metadata?.paginas_aprox
  );
}

function normalizeSelectedResource(resource) {
  if (!resource) return resource;
  const pageCount = getResourcePageCount(resource);
  const pageCountApprox = getResourcePageCountApprox(resource);
  const next = {
    ...resource,
    pageCount,
    pageCountApprox,
    pageRangeEnabled: !!resource?.pageRangeEnabled,
    pageFrom: resource?.pageFrom != null ? String(resource.pageFrom) : "",
    pageTo: resource?.pageTo != null ? String(resource.pageTo) : "",
  };

  if (pageCount === 1) {
    next.pageRangeEnabled = false;
    next.pageFrom = "";
    next.pageTo = "";
    return next;
  }

  if (!next.pageRangeEnabled) return next;

  const rawFrom = Number.parseInt(String(next.pageFrom || "").trim(), 10);
  const rawTo = Number.parseInt(String(next.pageTo || "").trim(), 10);
  let from = Number.isInteger(rawFrom) ? rawFrom : null;
  let to = Number.isInteger(rawTo) ? rawTo : null;

  if (from !== null && from < 1) from = 1;
  if (pageCount && pageCount > 1 && from !== null && from > pageCount - 1)
    from = pageCount - 1;
  const minTo = Number.isInteger(from) && from >= 1 ? from + 1 : 2;

  if (to !== null && to < minTo) to = minTo;
  if (pageCount && pageCount > 1 && to !== null && to > pageCount)
    to = pageCount;

  next.pageFrom = from !== null ? String(from) : "";
  next.pageTo = to !== null ? String(to) : "";
  return next;
}

function formatSelectedResourceMeta(resource) {
  const localSize = Number(resource?._localFile?.size || 0);
  const sizeTxt =
    resource?.size_formatted ||
    (resource?.tamaño_archivo
      ? `${Math.round(Number(resource.tamaño_archivo) / 1024)} KB`
      : localSize
        ? `${(localSize / 1024).toFixed(2)} KB`
        : "");

  const ext = getResourceExt(resource);
  const typeTxt =
    ext || (resource?.tipo_contenido ? String(resource.tipo_contenido) : "");
  const count = getResourcePageCount(resource);
  const approx = getResourcePageCountApprox(resource);
  const countTxt = Number.isInteger(count)
    ? ext === "pptx"
      ? `${count} diapositiva${count === 1 ? "" : "s"}`
      : `${count} pág.${count === 1 ? "" : "s"}`
    : "";
  let rangeTxt = "";
  if (canUseResourceRange(resource)) {
    if (resource?.pageRangeEnabled) {
      const from = Number.parseInt(String(resource?.pageFrom || "").trim(), 10);
      const to = Number.parseInt(String(resource?.pageTo || "").trim(), 10);
      if (Number.isInteger(from) && Number.isInteger(to)) {
        rangeTxt = `Págs. ${from}–${to}`;
      } else {
        rangeTxt = "Rango pendiente";
      }
    } else if (countTxt) {
      rangeTxt = "Todas las páginas";
    }
  }

  return [
    sizeTxt,
    typeTxt,
    countTxt ? `${countTxt}${approx ? " aprox." : ""}` : "",
    rangeTxt,
  ]
    .filter(Boolean)
    .join(" • ");
}


const CSS = `
/* =========================
   AIActivityQuestionsModal
   Scoped styles
========================= */
.aiq-modal-overlay{
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(2, 6, 23, .55);
  backdrop-filter: blur(6px);
}
.aiq-modal{
  max-width: 80%;
  max-height: min(86vh, 980px);
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, .10);
  box-shadow: 0 20px 60px rgba(2, 6, 23, .30);
  display: grid;
  grid-template-rows: auto 1fr auto;
}
.aiq-modal-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:14px 16px;
  border-bottom: 1px solid rgba(15,23,42,.08);
}
.aiq-title{
  display:flex;
  align-items:center;
  gap:10px;
  font-weight: 800;
  color:#0f172a;
  font-size: 16px;
  margin:0;
}
.aiq-icon{
  width:34px; height:34px;
  border-radius: 12px;
  background: linear-gradient(135deg, #22c55e, #3b82f6);
  display:grid; place-items:center;
  color:#fff;
  box-shadow: 0 10px 24px rgba(2,6,23,.20);
}
.aiq-close{
  border:0;
  background: transparent;
  cursor:pointer;
  padding:8px;
  border-radius: 12px;
}
.aiq-close:hover{ background: rgba(15,23,42,.06); }

.aiq-body{
  overflow:auto;
  padding: 14px 16px 8px;
}
.aiq-grid{
  display:grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
@media (max-width: 920px){
  .aiq-grid{ grid-template-columns: 1fr; }
}

.aiq-card{
  border:1px solid rgba(15,23,42,.10);
  border-radius: 16px;
  padding: 12px;
  background: #ffffff;
}
.aiq-card h4{
  margin:0 0 8px;
  font-size: 14px;
  color:#0f172a;
}
.aiq-muted{
  color: rgba(15,23,42,.65);
  font-size: 12px;
}
.aiq-row{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
@media (max-width: 520px){
  .aiq-row{ grid-template-columns: 1fr; }
}
.aiq-field label{
  display:block;
  font-size: 13px;
  font-weight: 700;
  color:#0f172a;
  margin-bottom: 6px;
}
.aiq-field input,
.aiq-field select,
.aiq-field textarea{
  width:100%;
  border: 1px solid rgba(15,23,42,.12);
  border-radius: 12px;
  padding: 10px 12px;
  outline:none;
  font-size: 13px;
  background:#fff;
}
.aiq-field textarea{ min-height: 96px; resize: vertical; }
.aiq-field input:focus,
.aiq-field select:focus,
.aiq-field textarea:focus{
  border-color: rgba(59,130,246,.40);
  box-shadow: 0 0 0 4px rgba(59,130,246,.12);
}

.aiq-chips{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top: 6px;
  overflow-wrap: normal !important; 
  hyphens: none !important; 
  word-break: unset !important; 
}
.aiq-chip{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding: 8px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15,23,42,.10);
  background: rgba(15,23,42,.03);
  font-size: 12px;
  cursor:pointer;
  user-select:none;
}
.aiq-chip input{ margin:0; }

.aiq-divider{
  height:1px;
  background: rgba(15,23,42,.08);
  margin: 10px 0;
}

.aiq-details{
  border: 1px dashed rgba(15,23,42,.18);
  border-radius: 16px;
  padding: 10px 12px;
  background: rgba(15,23,42,.02);
}
.aiq-details summary{
  cursor:pointer;
  list-style:none;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  font-weight: 800;
  color:#0f172a;
  font-size: 13px;
}
.aiq-details summary::-webkit-details-marker{ display:none; }

.aiq-footer{
  padding: 12px 16px;
  border-top: 1px solid rgba(15,23,42,.08);
  display:flex;
  align-items:center;
  justify-content: space-between;
  gap: 10px;
  background: #fff;
}
.aiq-err{
  color:#b91c1c;
  font-size: 12px;
  margin: 0;
}
.aiq-actions{
  display:flex;
  gap:10px;
  align-items:center;
}
.aiq-btn{
  border:0;
  cursor:pointer;
  border-radius: 12px;
  padding: 10px 12px;
  font-weight: 800;
  font-size: 13px;
}
.aiq-btn.secondary{
  background: rgba(15,23,42,.06);
  color:#0f172a;
}
.aiq-btn.primary{
  background: linear-gradient(135deg,#22c55e,#3b82f6);
  color:#fff;
  box-shadow: 0 14px 30px rgba(2,6,23,.22);
}
.aiq-btn:disabled{
  opacity:.65;
  cursor:not-allowed;
}
.aiq-help{
  display:inline-flex;
  align-items:center;
  gap:6px;
}
.aiq-help svg{ width:16px; height:16px; }


/* ===== Proceso IA (carga) ===== */
.aiq-process-overlay{
  position: fixed;
  inset: 0;
  z-index: 11000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(2, 6, 23, .55);
  backdrop-filter: blur(6px);
}
.aiq-process{
  width: min(720px, 100%);
  background: #fff;
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(2,6,23,.30);
  overflow: hidden;
}
.aiq-process-h{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(15,23,42,.08);
}
.aiq-process-title{ font-weight: 900; color:#0f172a; }
.aiq-process-close{
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(15,23,42,.10);
  background: transparent;
  cursor: pointer;
}
.aiq-process-close:disabled{ opacity:.45; cursor:not-allowed; }
.aiq-process-b{ padding: 14px 16px 16px; }
.aiq-process-sub{ color: rgba(15,23,42,.65); font-size: 13px; margin-bottom: 10px; }
.aiq-progress{
  height: 10px;
  background: rgba(15,23,42,.06);
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 999px;
  overflow: hidden;
}
.aiq-progress > div{
  height: 100%;
  width: 0%;
  background: linear-gradient(90deg, #3b82f6, #22c55e);
  transition: width .25s ease;
}
.aiq-steps{ list-style:none; padding:0; margin: 12px 0 0; display:grid; gap: 10px; }
.aiq-step{
  display:flex;
  align-items:flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 14px;
  background: rgba(15,23,42,.02);
}
.aiq-step-dot{
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(15,23,42,.12);
  display:grid;
  place-items:center;
  flex: 0 0 auto;
  margin-top: 2px;
  position: relative;
}
.aiq-step-title{ font-weight: 800; color:#0f172a; font-size: 13px; }
.aiq-step-state{ color: rgba(15,23,42,.65); font-size: 12px; margin-top: 2px; }
.aiq-step[data-state="active"] .aiq-step-dot{ border-color: rgba(59,130,246,.55); }
.aiq-step[data-state="active"] .aiq-step-dot::after{
  content:"";
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #3b82f6;
  animation: aiqPulse 1.1s ease-in-out infinite;
}
.aiq-step[data-state="done"] .aiq-step-dot{ border-color: rgba(34,197,94,.70); }
.aiq-step[data-state="done"] .aiq-step-dot::after{
  content:"✓";
  color: #16a34a;
  font-weight: 900;
  font-size: 13px;
  line-height: 1;
}
.aiq-step[data-state="error"] .aiq-step-dot{ border-color: rgba(239,68,68,.75); }
.aiq-step[data-state="error"] .aiq-step-dot::after{
  content:"!";
  color: #dc2626;
  font-weight: 900;
  font-size: 14px;
  line-height: 1;
}
.aiq-step[data-state="cancelled"] .aiq-step-dot{ border-color: rgba(245,158,11,.80); }
.aiq-step[data-state="cancelled"] .aiq-step-dot::after{
  content:"−";
  color: #d97706;
  font-weight: 900;
  font-size: 16px;
  line-height: 1;
}
@keyframes aiqPulse{
  0%{ transform: scale(.85); opacity: .55; }
  50%{ transform: scale(1); opacity: 1; }
  100%{ transform: scale(.85); opacity: .55; }
}
.aiq-process-foot{ display:flex; justify-content:flex-end; gap: 10px; margin-top: 14px; }

/* ===== Review de preguntas generadas ===== */
.aiq-review-overlay{ z-index: 12000; }
.aiq-review{
  width: min(980px, 100%);
  max-height: min(86vh, 980px);
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(15,23,42,.10);
  box-shadow: 0 20px 60px rgba(2,6,23,.30);
  display: grid;
  grid-template-rows: auto 1fr auto;
}

/* Lista estilo uploader.html */
.aiq-review-list{ overflow:auto; padding: 14px 16px; }
.aiq-bank-list{ list-style:none; padding:0; margin:0; display:grid; gap: 10px; }
.aiq-bank-item{
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 16px;
  background: rgba(15,23,42,.02);
  padding: 12px;
}
.aiq-bank-top{ display:flex; align-items:flex-start; justify-content:space-between; gap: 10px; }
.aiq-bank-header{ color: rgba(15,23,42,.65); font-size: 12px; font-weight: 800; }
.aiq-bank-enun{ margin-top: 6px; color:#0f172a; font-size: 13px; font-weight: 800; }
.aiq-clean{ list-style:none; padding-left: 0; margin: 8px 0 0; display:grid; gap: 6px; }
.aiq-clean li{ color: rgba(15,23,42,.85); font-size: 13px; }
.aiq-info{
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 14px;
  background: #fff;
  color: rgba(15,23,42,.75);
  font-size: 12px;
}
.aiq-review-empty{
  padding: 18px 12px;
  border: 1px dashed rgba(15,23,42,.18);
  border-radius: 16px;
  background: rgba(15,23,42,.02);
  color: rgba(15,23,42,.70);
  font-size: 13px;
  font-weight: 800;
  text-align:center;
}

.aiq-spin{ animation: aiqSpin 1s linear infinite; }
@keyframes aiqSpin{ from{ transform: rotate(0deg); } to{ transform: rotate(360deg); } }

.aiq-note{
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(245,158,11,.22);
  background: rgba(245,158,11,.08);
  color: #92400e;
  font-size: 12px;
  font-weight: 700;
}
.aiq-row-3{
  display:grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 10px;
}
@media (max-width: 720px){
  .aiq-row-3{ grid-template-columns: 1fr; }
}
.aiq-range{
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(15,23,42,.08);
}
.aiq-range-toggle{
  display:inline-flex;
  align-items:center;
  gap:6px;
  font-size: 12px;
  color: rgba(15,23,42,.78);
  font-weight: 700;
}
.aiq-range-fields{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}
.aiq-range-fields input{
  min-width: 0;
}
.aiq-hint{
  font-size: 12px;
  margin-top: 8px;
}
.aiq-fields-grid{
  display:grid;
  grid-template-columns: repeat(2, minmax(0,1fr));
  gap: 10px;
}
@media (max-width: 720px){
  .aiq-fields-grid{ grid-template-columns: 1fr; }
}
.aiq-chip.aiq-chip--bloom{
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
  flex-wrap: nowrap;
  justify-content: center;
}
.aiq-chip.aiq-chip--bloom input{
  flex: 0 0 auto;
}
.aiq-chip.aiq-chip--bloom .aiq-chip-text{
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aiq-bloom-grid{
  display:grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 10px;
  margin-top: 10px;
}
@media (max-width: 720px){
  .aiq-bloom-grid{ grid-template-columns: 1fr; }
}

/* ===== Configuracion de preguntas estilo uploader.html ===== */
.aiq-config-shell{
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 12px;
  padding: 8px;
  background: rgba(15,23,42,.015);
}
.aiq-config-titlebar{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.aiq-total-bar{
  display:inline-flex;
  align-items:center;
  gap: 7px;
  flex-wrap: wrap;
  padding: 4px;
  border-radius: 11px;
  border: 1px solid rgba(15,23,42,.10);
  background: rgba(15,23,42,.025);
}
.aiq-total-label{
  color: rgba(15,23,42,.62);
  font-size: 10px;
  font-weight: 800;
}
.aiq-total-presets{
  display:inline-flex;
  gap: 4px;
  flex-wrap: wrap;
}
.aiq-total-btn,
.aiq-mode-btn{
  border: 1px solid rgba(15,23,42,.10);
  background: rgba(255,255,255,.86);
  color: #0f172a;
  border-radius: 10px;
  min-height: 25px;
  padding: 4px 8px;
  font-size: 10.5px;
  font-weight: 800;
  cursor: pointer;
}
.aiq-total-btn.is-active,
.aiq-mode-btn.is-active{
  border-color: rgba(59,130,246,.40);
  background: rgba(59,130,246,.12);
  color: #1d4ed8;
  box-shadow: inset 0 0 0 1px rgba(59,130,246,.10);
}
.aiq-total-other{
  width: 58px;
  border: 1px solid rgba(15,23,42,.12);
  border-radius: 10px;
  padding: 5px 7px;
  font-size: 11px;
  font-weight: 800;
}
.aiq-main-reflow{
  display:grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 7px;
  margin-top: 8px;
}
.aiq-option-card{
  min-width:0;
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 11px;
  padding: 8px;
  background: #fff;
}
.aiq-option-heading{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.aiq-option-title{
  color:#0f172a;
  font-size: 11.5px;
  font-weight: 900;
}
.aiq-mode-switch{
  display:inline-flex;
  gap: 4px;
  padding: 2px;
  border-radius: 10px;
  border: 1px solid rgba(15,23,42,.10);
  background: rgba(15,23,42,.025);
}
.aiq-mode-grid{
  display:grid;
  gap: 5px;
}
.aiq-type-grid{
  grid-template-columns: repeat(2, minmax(0,1fr));
}
.aiq-diff-grid{
  grid-template-columns: repeat(2, minmax(0,1fr));
}
.aiq-style-grid{
  grid-template-columns: repeat(2, minmax(0,1fr));
}
.aiq-bloom-count-grid{
  grid-template-columns: repeat(3, minmax(0,1fr));
}
.aiq-count-item{
  position:relative;
  display:grid;
  grid-template-columns: minmax(0,1fr) 62px;
  align-items:center;
  gap: 5px;
  min-width:0;
  padding: 5px 6px;
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 9px;
  background: rgba(15,23,42,.018);
}
.aiq-count-item.is-auto-selected{
  border-color: rgba(59,130,246,.26);
  background: rgba(59,130,246,.075);
}
.aiq-count-item input[type="checkbox"]{
  margin:0;
}
.aiq-count-label{
  min-width:0;
  display:flex;
  align-items:center;
  gap: 5px;
  color: rgba(15,23,42,.78);
  font-size: 10px;
  font-weight: 800;
}
.aiq-count-label span{
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.aiq-count-item input[type="number"]{
  width:100%;
  min-width:0;
  border: 1px solid rgba(15,23,42,.12);
  border-radius: 8px;
  padding: 5px 6px;
  color:#0f172a;
  background:#fff;
  font-size: 10.5px;
  font-weight: 800;
}
.aiq-count-item input[type="number"]:disabled{
  color: rgba(15,23,42,.72);
  background: rgba(255,255,255,.62);
  opacity: 1;
}
.aiq-config-hint{
  font-size: 10px;
  line-height: 1.25;
  margin: 6px 0 0;
}
.aiq-advanced-grid{
  display:grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 7px;
  align-items: stretch;
}
.aiq-advanced-card{
  height:100%;
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 11px;
  padding: 8px;
  background: rgba(15,23,42,.015);
}
.aiq-advanced-card h4{
  margin: 0 0 8px;
  font-size: 11.5px;
}
.aiq-config-shell .aiq-muted,
.aiq-details .aiq-muted{
  font-size: 10px;
  line-height: 1.25;
}
.aiq-config-shell h4,
.aiq-details h4{
  line-height: 1.15;
}
.aiq-details{
  padding: 8px 10px;
  border-radius: 12px;
}
.aiq-details summary{
  font-size: 11.5px;
}
.aiq-details .aiq-row{
  gap: 7px;
}
.aiq-details .aiq-field label{
  font-size: 10.5px;
  margin-bottom: 4px;
}
.aiq-details .aiq-field input,
.aiq-details .aiq-field select,
.aiq-details .aiq-field textarea{
  padding: 7px 8px;
  border-radius: 9px;
  font-size: 11px;
}
.aiq-details .aiq-chip{
  padding: 5px 7px;
  font-size: 10.5px;
}
.aiq-inline-check-row{
  display:grid;
  grid-template-columns: minmax(0,1fr) 150px;
  gap: 6px;
  margin-top: 8px;
  align-items:end;
}
@media (max-width: 1040px){
  .aiq-main-reflow,
  .aiq-advanced-grid{ grid-template-columns: 1fr; }
}
@media (max-width: 620px){
  .aiq-type-grid,
  .aiq-diff-grid,
  .aiq-style-grid,
  .aiq-bloom-count-grid,
  .aiq-inline-check-row{ grid-template-columns: 1fr; }
  .aiq-total-bar{ width:100%; }
}

`;

export default function AIActivityQuestionsModal({
  isOpen,
  onClose,
  onGenerate,
  onSubmit,
  onDeleteQuestion,
  // Nuevo: confirmar antes de agregar a la tarea/banco.
  // Si se proporciona, se mostrará un botón "Agregar preguntas a tarea" en la revisión.
  onAddToTask,
  parentId,
  // Si quieres que los recursos se asocien al MÓDULO padre aunque el modal se abra en una tarea,
  // pasa aquí el ID del módulo (resuelto en el frontend desde la estructura del curso).
  resourcesParentId,
  resourceParentOptions = [],
  resourceParentLabel = "Apartado de recursos",
  allowTemporaryFiles = false,
}) {
  // ====== Base ======
  // ID efectivo para listar/subir recursos.
  // Si el modal se abre en una tarea pero la BD solo permite recursos en "modulo",
  // pásanos resourcesParentId desde GestionarCurso y usaremos ese.
  const initialResourceParentId = resourcesParentId || parentId || null;
  const [selectedResourceParentId, setSelectedResourceParentId] = useState(
    initialResourceParentId,
  );
  const resourcesEstructuraId = selectedResourceParentId || null;
  const tempFilesMode = !!allowTemporaryFiles && !resourcesEstructuraId;

  const [count, setCount] = useState(10);

  const [useUnica, setUseUnica] = useState(true);
  const [useMultiple, setUseMultiple] = useState(true);
  const [useVF, setUseVF] = useState(false);
  const [useCompletar, setUseCompletar] = useState(false);

  const [tiposManual, setTiposManual] = useState({
    unica: 0,
    multiple: 0,
    vf: 0,
    completar: 0,
  });
  const [useAutoTipos, setUseAutoTipos] = useState(true);

  const [bloomMode, setBloomMode] = useState("auto");
  const [enabledBloom, setEnabledBloom] = useState({
    recordar: true,
    comprender: true,
    aplicar: true,
    analizar: true,
    evaluar: true,
    crear: true,
  });
  const [bloomCounts, setBloomCounts] = useState({
    recordar: 2,
    comprender: 2,
    aplicar: 2,
    analizar: 2,
    evaluar: 1,
    crear: 1,
  });

  const [useOCR, setUseOCR] = useState(false);
  const [useBert, setUseBert] = useState(false);

  const [prompt, setPrompt] = useState("");

  // ====== Dificultad ======
  const [diffMode, setDiffMode] = useState("auto"); // auto | custom
  const [enabledDiff, setEnabledDiff] = useState({
    facil: true,
    media: true,
    dificil: true,
  });
  const [diffEasy, setDiffEasy] = useState(4);
  const [diffMed, setDiffMed] = useState(4);
  const [diffHard, setDiffHard] = useState(2);

  // ====== Avanzado (como uploader.html) ======
  const [tolerancia, setTolerancia] = useState(1);
  const [maxReparaciones, setMaxReparaciones] = useState(1);

  const [styleMode, setStyleMode] = useState("auto");
  const [enabledStyle, setEnabledStyle] = useState({
    conceptual: true,
    caso_practico: true,
    comparacion: true,
    trampa_comun: true,
  });
  const [styleCounts, setStyleCounts] = useState({
    conceptual: 4,
    caso_practico: 3,
    comparacion: 2,
    trampa_comun: 1,
  });
  const [contextoCaso, setContextoCaso] = useState(false);
  const [longitudContexto, setLongitudContexto] = useState("");

  const [fbModo, setFbModo] = useState("");
  const [fbExplainBad, setFbExplainBad] = useState(false);

  const [incluirEvidencia, setIncluirEvidencia] = useState(false);

  const [numOpciones, setNumOpciones] = useState("");
  const [cantidadCorrectas, setCantidadCorrectas] = useState("");
  const [distractores, setDistractores] = useState("");
  const [evitarAbsurdos, setEvitarAbsurdos] = useState(true);

  const [palabrasRespuesta, setPalabrasRespuesta] = useState("");

  // ====== UX state ======
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ====== Proceso + revisión (como uploader.html) ======
  const [procOpen, setProcOpen] = useState(false);
  const [procDone, setProcDone] = useState(false);
  const [procTitle, setProcTitle] = useState("Procesando…");
  const [procSubtitle, setProcSubtitle] = useState("Iniciando…");
  const [procSteps, setProcSteps] = useState([]);
  const [procStates, setProcStates] = useState([]); // pending|active|done|error

  const [reviewOpen, setReviewOpen] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const [addingToTask, setAddingToTask] = useState(false);

  const addLabel = parentId ? "Agregar preguntas a tarea" : "Agregar preguntas";

  const procTimersRef = useRef([]);
  const generationAbortRef = useRef(null);
  const generationRunIdRef = useRef(0);
  const [procCancelling, setProcCancelling] = useState(false);
  const [procCancelled, setProcCancelled] = useState(false);


  // Cleanup timers al desmontar
  useEffect(() => {
    return () => {
      try {
        (procTimersRef.current || []).forEach((t) => clearTimeout(t));
      } catch {
        /* no-op */
      }
      try {
        generationAbortRef.current?.abort?.();
      } catch {
        /* no-op */
      }
    };
  }, []);

  // ====== Recursos seleccionados (para enviar a API_IA) ======
  const API_BASE = useMemo(
    () => (process.env.REACT_APP_API_URL || "").replace(/\/+$|\s+/g, ""),
    [],
  );
  const getAuthToken = () => {
    try {
      return (
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("token") ||
        sessionStorage.getItem("accessToken") ||
        ""
      );
    } catch {
      return "";
    }
  };

  const [showResourcePicker, setShowResourcePicker] = useState(false);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesErr, setResourcesErr] = useState("");
  const [resources, setResources] = useState([]);
  const [resourcesSearch, setResourcesSearch] = useState("");
  const [selectedResources, setSelectedResources] = useState([]);

  const fileInputRef = useRef(null);
  const [uploadingExternal, setUploadingExternal] = useState(false);
  const [uploadExternalErr, setUploadExternalErr] = useState("");
  const [temporaryFiles, setTemporaryFiles] = useState([]);

  const hasGenerationMaterial = tempFilesMode
    ? (temporaryFiles?.length || 0) > 0
    : (selectedResources?.length || 0) > 0;
  // [{id,titulo,nombre_original,nombre_archivo,tipo_contenido,tamaño_archivo,size_formatted}]

  // Inject CSS una sola vez
  useEffect(() => {
    const id = "aiq-modal-css";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = CSS;
      document.head.appendChild(tag);
    }
  }, []);

  // Reset liviano al abrir
  useEffect(() => {
    if (!isOpen) return;
    setErr("");
    setLoading(false);
    setProcCancelling(false);
    setProcCancelled(false);
    try {
      generationAbortRef.current?.abort?.();
    } catch {
      /* no-op */
    }
    generationAbortRef.current = null;
    setTemporaryFiles([]);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedResourceParentId(resourcesParentId || parentId || null);
  }, [isOpen, resourcesParentId, parentId]);

  useEffect(() => {
    if (!isOpen || !selectedResources?.length) return;

    let cancelled = false;

    const hydrate = async () => {
      const hydrated = await Promise.all(
        selectedResources.map(async (resource) => {
          if (
            !resource ||
            Number.isInteger(getResourcePageCount(resource)) ||
            !canUseResourceRange(resource) ||
            !resource?._localFile
          ) {
            return resource;
          }

          const meta = await readResourcePageCountFromFile(resource._localFile);
          if (!meta || !Number.isInteger(meta.count) || meta.count <= 0) {
            return resource;
          }

          return normalizeSelectedResource({
            ...resource,
            pageCount: meta.count,
            pageCountApprox: !!meta.approx,
          });
        }),
      );

      if (cancelled) return;
      const changed = hydrated.some((resource, idx) => {
        const prev = selectedResources[idx];
        return (
          getResourcePageCount(prev) !== getResourcePageCount(resource) ||
          getResourcePageCountApprox(prev) !==
          getResourcePageCountApprox(resource)
        );
      });

      if (changed) setSelectedResources(hydrated);
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedResources]);

  useEffect(() => {
    if (!isOpen) return;
    if (
      !Array.isArray(resourceParentOptions) ||
      resourceParentOptions.length === 0
    )
      return;

    const currentId = Number(selectedResourceParentId);
    const exists = resourceParentOptions.some(
      (opt) => Number(opt.id) === currentId,
    );

    if (!exists) {
      setSelectedResourceParentId(Number(resourceParentOptions[0].id));
      setResourcesSearch("");
      setResourcesErr("");
    }
  }, [isOpen, resourceParentOptions, selectedResourceParentId]);

  const enabledKeys = useMemo(() => {
    const keys = [];
    if (useUnica) keys.push("unica");
    if (useMultiple) keys.push("multiple");
    if (useVF) keys.push("vf");
    if (useCompletar) keys.push("completar");
    return keys;
  }, [useUnica, useMultiple, useVF, useCompletar]);

  const tiposFinal = useMemo(() => {
    const c = clampInt(count, 1, 50);
    if (useAutoTipos) return distributeEvenly(c, enabledKeys);
    return {
      unica: clampInt(tiposManual.unica),
      multiple: clampInt(tiposManual.multiple),
      vf: clampInt(tiposManual.vf),
      completar: clampInt(tiposManual.completar),
    };
  }, [count, useAutoTipos, enabledKeys, tiposManual]);

  const sumaTipos = useMemo(() => sumObj(tiposFinal), [tiposFinal]);

  const totalForConfig = useMemo(() => {
    return useAutoTipos ? clampInt(count, 1, 50) : sumaTipos;
  }, [useAutoTipos, count, sumaTipos]);

  const tiposHint = useMemo(() => {
    const colors = getThemeAwareHintColors();
    if (useAutoTipos && !enabledKeys.length) {
      return {
        text: "⚠️ Debes dejar al menos un tipo de pregunta marcado.",
        color: colors.warn,
      };
    }
    if (useAutoTipos) {
      const autoCounts = normalizeQuestionTypeCounts(count, enabledKeys);
      const parts = enabledKeys.map((key) => {
        const labels = {
          unica: "Única",
          multiple: "Múltiple",
          vf: "V/F",
          completar: "Completar",
        };
        return `${labels[key]}: ${autoCounts[key] || 0}`;
      });
      return {
        text: parts.length
          ? `✅ Distribución automática lista: ${parts.join(" · ")}.`
          : "El sistema calculará la mezcla automáticamente.",
        color: colors.ok,
      };
    }
    if (!sumaTipos) {
      return {
        text: "Escribe cantidades por tipo. La suma será el total enviado.",
        color: colors.neutral,
      };
    }
    const parts = Object.entries(tiposFinal)
      .filter(([, n]) => Number(n) > 0)
      .map(
        ([key, n]) =>
          `${{ unica: "Única", multiple: "Múltiple", vf: "V/F", completar: "Completar" }[key]}: ${n}`,
      );
    return {
      text: `✅ Total manual: ${sumaTipos} pregunta(s).${parts.length ? " " + parts.join(" · ") : ""}`,
      color: colors.ok,
    };
  }, [enabledKeys, useAutoTipos, count, sumaTipos, tiposFinal]);

  const bloomHint = useMemo(() => {
    const colors = getThemeAwareHintColors();
    if (bloomMode === "auto") {
      const activeLevels = BLOOM_LEVELS.filter((lvl) => enabledBloom[lvl]);
      const counts = distributeCounts(totalForConfig, activeLevels, {
        recordar: 0,
        comprender: 0,
        aplicar: 0,
        analizar: 0,
        evaluar: 0,
        crear: 0,
      });
      const parts = activeLevels.map((lvl) => `${lvl}: ${counts[lvl] || 0}`);
      return {
        text: parts.length
          ? `Automático: Bloom se reparte entre ${parts.join(" · ")}` 
          : "Automático: selecciona al menos un nivel de Bloom.",
        color: colors.neutral,
      };
    }

    const sum = Object.values(bloomCounts).reduce(
      (acc, n) => acc + Math.max(0, Number(n) || 0),
      0,
    );
    if (!sum)
      return {
        text: "Escribe cantidades por nivel de Bloom.",
        color: colors.neutral,
      };
    if (totalForConfig && sum !== totalForConfig) {
      return {
        text: `La suma de Bloom es ${sum}, pero el total enviado será ${totalForConfig}. Deben coincidir.`,
        color: colors.warn,
      };
    }
    const active = Object.entries(bloomCounts)
      .filter(([, n]) => Number(n) > 0)
      .map(([lvl, n]) => `${lvl}: ${n}`);
    return {
      text: `Bloom manual: ${active.join(" · ")}`,
      color: colors.ok,
    };
  }, [bloomMode, bloomCounts, totalForConfig, enabledBloom]);

  const styleHint = useMemo(() => {
    const colors = getThemeAwareHintColors();
    if (styleMode === "auto") {
      const active = STYLE_KEYS.filter((key) => enabledStyle[key])
        .map((key) => STYLE_LABELS[key] || key)
        .join(" · ");
      return {
        text: active
          ? `Automático: el enfoque se reparte entre ${active}.`
          : "Automático: selecciona al menos un enfoque.",
        color: colors.neutral,
      };
    }

    const sum = Object.values(styleCounts).reduce(
      (acc, n) => acc + Math.max(0, Number(n) || 0),
      0,
    );
    if (!sum) {
      return { text: "Escribe cantidades por estilo.", color: colors.neutral };
    }
    if (totalForConfig && sum !== totalForConfig) {
      return {
        text: `La suma de estilos es ${sum}, pero el total enviado será ${totalForConfig}. Deben coincidir.`,
        color: colors.warn,
      };
    }
    const active = Object.entries(styleCounts)
      .filter(([, n]) => Number(n) > 0)
      .map(([key, n]) => `${STYLE_LABELS[key] || key}: ${n}`);
    return {
      text: `Estilo manual: ${active.join(" · ")}`,
      color: colors.ok,
    };
  }, [styleMode, styleCounts, totalForConfig, enabledStyle]);

  const diffHint = useMemo(() => {
    const colors = getThemeAwareHintColors();
    if (diffMode === "auto") {
      const active = ["facil", "media", "dificil"]
        .filter((key) => enabledDiff[key])
        .map((key) => ({ facil: "fáciles", media: "medias", dificil: "difíciles" }[key]))
        .join(" · ");
      return {
        text: active
          ? `Automático: la dificultad se reparte entre ${active}.`
          : "Automático: selecciona al menos una dificultad.",
        color: colors.neutral,
      };
    }
    const e = clampInt(diffEasy, 0, 50);
    const m = clampInt(diffMed, 0, 50);
    const h = clampInt(diffHard, 0, 50);
    const sum = e + m + h;
    if (!sum) return { text: "Escribe cantidades (ej: 4,4,2).", color: colors.neutral };
    if (totalForConfig && sum !== totalForConfig) {
      return {
        text: `La suma de dificultad es ${sum}, pero el total es ${totalForConfig}. Deben coincidir.`,
        color: colors.warn,
      };
    }
    return {
      text: `Dificultad personalizada: ${e} fáciles, ${m} medias, ${h} difíciles.`,
      color: colors.ok,
    };
  }, [diffMode, diffEasy, diffMed, diffHard, totalForConfig, enabledDiff]);

  const totalPresets = [6, 10, 15, 20];
  const typeCountValues = useAutoTipos ? tiposFinal : tiposManual;
  const diffAutoCounts = useMemo(
    () =>
      distributeCounts(totalForConfig, ["facil", "media", "dificil"].filter((key) => enabledDiff[key]), {
        facil: 0,
        media: 0,
        dificil: 0,
      }),
    [totalForConfig, enabledDiff],
  );
  const styleAutoCounts = useMemo(
    () =>
      distributeCounts(totalForConfig, STYLE_KEYS.filter((key) => enabledStyle[key]), {
        conceptual: 0,
        caso_practico: 0,
        comparacion: 0,
        trampa_comun: 0,
      }),
    [totalForConfig, enabledStyle],
  );
  const bloomAutoCounts = useMemo(
    () =>
      distributeCounts(totalForConfig, BLOOM_LEVELS.filter((key) => enabledBloom[key]), {
        recordar: 0,
        comprender: 0,
        aplicar: 0,
        analizar: 0,
        evaluar: 0,
        crear: 0,
      }),
    [totalForConfig, enabledBloom],
  );
  const displayedDiffCounts =
    diffMode === "auto"
      ? diffAutoCounts
      : { facil: diffEasy, media: diffMed, dificil: diffHard };
  const displayedStyleCounts = styleMode === "auto" ? styleAutoCounts : styleCounts;
  const displayedBloomCounts = bloomMode === "auto" ? bloomAutoCounts : bloomCounts;

  const setQuestionTotal = (value) => {
    const next = clampInt(value, 1, 50);
    setCount(next);
    if (!useAutoTipos) {
      setTiposManual((prev) =>
        distributeCounts(next, ["unica", "multiple", "vf", "completar"], prev),
      );
    }
    if (diffMode === "custom") {
      const nextDiff = distributeCounts(next, ["facil", "media", "dificil"], {
        facil: diffEasy,
        media: diffMed,
        dificil: diffHard,
      });
      setDiffEasy(nextDiff.facil);
      setDiffMed(nextDiff.media);
      setDiffHard(nextDiff.dificil);
    }
    if (styleMode === "custom") {
      setStyleCounts((prev) => distributeCounts(next, STYLE_KEYS, prev));
    }
    if (bloomMode === "custom") {
      setBloomCounts((prev) => distributeCounts(next, BLOOM_LEVELS, prev));
    }
  };

  const switchTypeMode = (mode) => {
    const auto = mode === "auto";
    setUseAutoTipos(auto);
    if (!auto) {
      setTiposManual(
        distributeCounts(clampInt(count, 1, 50), ["unica", "multiple", "vf", "completar"], tiposManual),
      );
    }
  };

  const switchDiffMode = (mode) => {
    setDiffMode(mode);
    if (mode === "custom") {
      const next = distributeCounts(totalForConfig, ["facil", "media", "dificil"], {
        facil: diffEasy,
        media: diffMed,
        dificil: diffHard,
      });
      setDiffEasy(next.facil);
      setDiffMed(next.media);
      setDiffHard(next.dificil);
    }
  };

  const switchStyleMode = (mode) => {
    setStyleMode(mode);
    if (mode === "custom") {
      setStyleCounts((prev) => distributeCounts(totalForConfig, STYLE_KEYS, prev));
    }
  };

  const switchBloomMode = (mode) => {
    setBloomMode(mode);
    if (mode === "custom") {
      setBloomCounts((prev) => distributeCounts(totalForConfig, BLOOM_LEVELS, prev));
    }
  };

  const updateManualTypeCount = (key, value) => {
    setTiposManual((prev) =>
      clampGroupToTotal(prev, key, value, clampInt(count, 1, 50)),
    );
  };

  const updateStyleCount = (key, value) => {
    setStyleCounts((prev) => clampGroupToTotal(prev, key, value, totalForConfig));
  };

  const updateBloomCount = (key, value) => {
    setBloomCounts((prev) => clampGroupToTotal(prev, key, value, totalForConfig));
  };

  const toggleEnabledMap = (setter, current, key) => {
    const activeCount = Object.values(current).filter(Boolean).length;
    if (current[key] && activeCount <= 1) return;
    setter((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearProcTimers = () => {
    try {
      (procTimersRef.current || []).forEach((t) => clearTimeout(t));
    } catch {
      /* no-op */
    }
    procTimersRef.current = [];
  };

  const setStepState = (index, state) => {
    setProcStates((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      if (index >= 0 && index < next.length) next[index] = state;
      return next;
    });
  };

  const openProcess = ({
    title = "Procesando…",
    subtitle = "Iniciando…",
    steps,
  } = {}) => {
    clearProcTimers();
    const s = steps || [
      "Preparando configuración",
      "Procesando material",
      "Enviando material",
      "Generando preguntas con IA",
    ];
    setProcTitle(title);
    setProcSubtitle(subtitle);
    setProcSteps(s);
    setProcStates(s.map(() => "pending"));
    setProcDone(false);
    setProcCancelled(false);
    setProcOpen(true);

    // Animación suave (UX)
    const t1 = setTimeout(() => {
      setStepState(0, "active");
      setProcSubtitle("Armando parámetros…");
    }, 120);
    const t2 = setTimeout(() => {
      setStepState(0, "done");
      setStepState(1, "active");
      setProcSubtitle("Enviando material y contexto…");
    }, 650);
    const t3 = setTimeout(() => {
      setStepState(1, "done");
      setStepState(2, "active");
      setProcSubtitle("Analizando material y contexto...");
    }, 1500);
    const t4 = setTimeout(() => {
      setStepState(2, "done");
      setStepState(3, "active");
      setProcSubtitle("Generando preguntas...");
    }, 3600);

    procTimersRef.current = [t1, t2, t3, t4];
  };

  const finishProcess = (ok = true, message, status = ok ? "done" : "error") => {
    clearProcTimers();
    setProcDone(true);
    setProcCancelling(false);
    setProcCancelled(status === "cancelled");
    setProcSubtitle(
      message || (status === "cancelled" ? "Generación cancelada." : ok ? "Proceso completado ✅" : "Ocurrió un error."),
    );
    setProcStates((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      if (status === "cancelled") {
        return next.map((s) => (s === "done" ? "done" : "cancelled"));
      }
      if (ok) {
        return next.map((s) => (s === "error" ? "error" : "done"));
      }
      // Si falla, deja el último activo como error
      const activeIdx = next.lastIndexOf("active");
      if (activeIdx >= 0) next[activeIdx] = "error";
      return next;
    });
  };

  const extractQuestionsFromResponse = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;

    const candidates = [
      res.questions,
      res.items,
      res.created,
      res.created_questions,
      res.preguntas,
      res.data?.questions,
      res.data?.items,
      res.data?.preguntas,
    ];
    for (const c of candidates) {
      if (Array.isArray(c) && c.length) return c;
    }
    // algunos backends devuelven { result: { questions: [...] } }
    const deep =
      res.result?.questions || res.result?.items || res.result?.preguntas;
    return Array.isArray(deep) ? deep : [];
  };

  const openResourcePicker = async () => {
    setResourcesErr("");
    setResourcesSearch("");

    if (!resourcesEstructuraId) {
      setResourcesErr(
        "No se detectó el contexto para recursos. Selecciona un apartado válido para listar o subir material.",
      );
      setShowResourcePicker(true);
      return;
    }

    setShowResourcePicker(true);
    setResourcesLoading(true);
    try {
      await fetchResourcesList();
    } catch (e) {
      setResourcesErr(e?.message || "Error cargando recursos");
      setResources([]);
    } finally {
      setResourcesLoading(false);
    }
  };

  const toggleResourceSelection = (r) => {
    if (!r?.id) return;
    const normalized = normalizeSelectedResource(r);
    setSelectedResources((prev) => {
      const exists = prev.some((x) => x.id === r.id);
      if (exists) return prev.filter((x) => x.id !== r.id);
      return [...prev, normalized];
    });
  };

  const updateSelectedResource = (id, updater) => {
    setSelectedResources((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const next =
          typeof updater === "function"
            ? updater(item)
            : { ...item, ...updater };
        return normalizeSelectedResource(next);
      }),
    );
  };

  const removeSelectedResource = (id) => {
    setSelectedResources((prev) => prev.filter((x) => x.id !== id));
  };

  const fetchResourcesList = async () => {
    if (!resourcesEstructuraId) return [];
    const res = await authService.authenticatedRequest(
      `/api/admin/estructura/${encodeURIComponent(resourcesEstructuraId)}/resources`,
      { method: "GET" },
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        json?.message ||
        json?.error ||
        `No se pudieron cargar recursos (HTTP ${res.status})`,
      );
    }
    const rawList = Array.isArray(json?.resources)
      ? json.resources
      : Array.isArray(json)
        ? json
        : [];
    const list = rawList.map((item) => normalizeSelectedResource(item));
    setResources(list);
    return list;
  };

  const triggerExternalUpload = () => {
    setUploadExternalErr("");
    if (!resourcesEstructuraId && !tempFilesMode) {
      setUploadExternalErr("No se detectó el contexto para subir recursos.");
      return;
    }
    fileInputRef.current?.click?.();
  };

  const handleExternalFilesSelected = async (e) => {
    const files = Array.from(e?.target?.files || []).filter(Boolean);
    try {
      e.target.value = "";
    } catch { }
    if (!files.length) return;
    if (tempFilesMode) {
      setTemporaryFiles((prev) => {
        const next = [...(prev || [])];
        const seen = new Set(next.map((f) => `${f.name}:${f.size}:${f.lastModified}`));
        files.forEach((file) => {
          const key = `${file.name}:${file.size}:${file.lastModified}`;
          if (!seen.has(key)) {
            seen.add(key);
            next.push(file);
          }
        });
        return next;
      });
      return;
    }
    await uploadExternalFiles(files);
  };

  const uploadExternalFiles = async (files) => {
    setUploadExternalErr("");
    if (!resourcesEstructuraId) {
      if (tempFilesMode) {
        setTemporaryFiles((prev) => [...(prev || []), ...(files || [])]);
        return;
      }
      setUploadExternalErr("No se detectó el contexto para subir recursos.");
      return;
    }

    setUploadingExternal(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      formData.append("activo", "true");

      const res = await authService.authenticatedRequest(
        `/api/admin/estructura/${encodeURIComponent(resourcesEstructuraId)}/resources`,
        { method: "POST", body: formData, dedupe: false },
      );

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          json?.message ||
          json?.error ||
          `No se pudo subir el material (HTTP ${res.status})`,
        );
      }

      // El backend puede devolver distintos formatos; intentamos extraer resources si vienen.
      const uploaded = Array.isArray(json?.resources)
        ? json.resources
        : Array.isArray(json)
          ? json
          : Array.isArray(json?.data?.resources)
            ? json.data.resources
            : [];

      // Refrescar lista para que aparezcan en el picker
      let latest = [];
      try {
        latest = await fetchResourcesList();
      } catch {
        // si falla el GET, igual seguimos con lo que tengamos
      }

      // Seleccionar automáticamente los recién subidos
      const fileNames = new Set(
        files.map((f) => String(f?.name || "").toLowerCase()).filter(Boolean),
      );
      const candidates = uploaded.length ? uploaded : latest;
      const autoSelect = (candidates || []).filter((r) => {
        const n1 = String(
          r?.nombre_original || r?.titulo || r?.nombre_archivo || "",
        ).toLowerCase();
        const n2 = String(r?.nombre_archivo || "").toLowerCase();
        return (n1 && fileNames.has(n1)) || (n2 && fileNames.has(n2));
      });

      if (autoSelect.length) {
        setSelectedResources((prev) => {
          const map = new Map(prev.map((x) => [x.id, x]));
          autoSelect.forEach((r) => {
            if (r?.id != null) {
              const matchingFile = files.find((f) => {
                const targetName = String(
                  r?.nombre_original || r?.titulo || r?.nombre_archivo || "",
                ).toLowerCase();
                return String(f?.name || "").toLowerCase() === targetName;
              });
              map.set(
                r.id,
                normalizeSelectedResource(
                  matchingFile ? { ...r, _localFile: matchingFile } : r,
                ),
              );
            }
          });
          return Array.from(map.values());
        });
      }

      // Si no matcheó por nombre, al menos avisar éxito y dejarlo disponible en el picker
    } catch (e) {
      setUploadExternalErr(
        e?.message || "No se pudo subir el material externo.",
      );
    } finally {
      setUploadingExternal(false);
    }
  };

  const removeTemporaryFile = (index) => {
    setTemporaryFiles((prev) => (prev || []).filter((_, i) => i !== index));
  };

  const handleCancelGeneration = () => {
    if (procDone || procCancelling) return;
    generationRunIdRef.current += 1;
    setProcCancelling(true);
    setGeneratedQuestions([]);
    setReviewOpen(false);
    setProcSubtitle("Cancelando generación…");
    try {
      generationAbortRef.current?.abort?.();
    } catch {
      /* no-op */
    }
    finishProcess(false, "Generación cancelada por el usuario.", "cancelled");
    setErr("Generación cancelada por el usuario.");
  };

  const handleGenerate = async () => {
    setErr("");
    const submitFn = onSubmit || onGenerate;

    if (!hasGenerationMaterial) {
      setErr(
        tempFilesMode
          ? "Primero sube al menos un documento temporal para generar preguntas."
          : "Primero selecciona o sube al menos un documento para generar preguntas.",
      );
      return;
    }

    const c = clampInt(count, 1, 50);
    if (useAutoTipos && !enabledKeys.length) {
      setErr("Selecciona al menos un tipo de pregunta.");
      return;
    }

    if (!useAutoTipos && sumaTipos <= 0) {
      setErr("Define al menos una pregunta en la distribución manual por tipo.");
      return;
    }

    const totalConfig = useAutoTipos ? c : sumaTipos;

    let dificultad_objetivo = null;
    if (diffMode === "custom") {
      const e = clampInt(diffEasy, 0, 50);
      const m = clampInt(diffMed, 0, 50);
      const h = clampInt(diffHard, 0, 50);
      const s = e + m + h;
      if (s && s !== totalConfig) {
        setErr(`La dificultad personalizada suma ${s}, pero el total enviado será ${totalConfig}. Ajusta fácil/media/difícil.`);
        return;
      }
      if (s > 0) {
        const base = s;
        dificultad_objetivo = {
          baja: Number((Math.max(0, e) / base).toFixed(4)),
          media: Number((Math.max(0, m) / base).toFixed(4)),
          alta: Number((Math.max(0, h) / base).toFixed(4)),
        };
      }
    } else {
      const activeDiff = ["facil", "media", "dificil"].filter((key) => enabledDiff[key]);
      if (!activeDiff.length) {
        setErr("Selecciona al menos una dificultad.");
        return;
      }
      const counts = distributeCounts(totalConfig, activeDiff, {
        facil: 0,
        media: 0,
        dificil: 0,
      });
      const base = Math.max(1, sumObj(counts));
      dificultad_objetivo = {
        baja: Number((Math.max(0, counts.facil || 0) / base).toFixed(4)),
        media: Number((Math.max(0, counts.media || 0) / base).toFixed(4)),
        alta: Number((Math.max(0, counts.dificil || 0) / base).toFixed(4)),
      };
    }

    let estilos = STYLE_KEYS.filter((key) => enabledStyle[key]);
    let estilo_objetivo = null;
    let estiloFinal = null;
    const styleCountsSum = Object.values(styleCounts).reduce(
      (acc, n) => acc + Math.max(0, Number(n) || 0),
      0,
    );

    if (styleMode === "custom") {
      estilos = STYLE_KEYS.filter((key) => (Number(styleCounts[key]) || 0) > 0);
      if (styleCountsSum > 0) estilo_objetivo = { ...styleCounts };
      if (styleCountsSum && styleCountsSum !== totalConfig) {
        setErr(`La distribución manual de estilos suma ${styleCountsSum}, pero el total enviado será ${totalConfig}. Ajusta los estilos para que coincida.`);
        return;
      }
    } else {
      if (!estilos.length) {
        setErr("Selecciona al menos un enfoque.");
        return;
      }
      estilo_objetivo = distributeCounts(totalConfig, estilos, {
        conceptual: 0,
        caso_practico: 0,
        comparacion: 0,
        trampa_comun: 0,
      });
    }
    if (estilos.length === 1) estiloFinal = estilos[0];

    const opciones_config = (() => {
      const payload = {};
      const num_opciones = numOpciones === "" ? null : clampInt(numOpciones, 2, 8);
      const correctas = cantidadCorrectas === "" ? null : clampInt(cantidadCorrectas, 1, 8);
      const distractoresValue = trimOrNull(distractores);

      if (num_opciones != null) payload.num_opciones = num_opciones;
      if (correctas != null) {
        payload.multiple_correctas_min = correctas;
        payload.multiple_correctas_max = correctas;
      }
      if (distractoresValue != null) payload.distractores = distractoresValue;
      if (evitarAbsurdos) payload.evitar_distractores_absurdos = true;

      return Object.keys(payload).length ? payload : null;
    })();

    const completar_config = (() => {
      const palabras = palabrasRespuesta === "" ? null : clampInt(palabrasRespuesta, 1, 20);
      return palabras != null && palabras > 0 ? { palabras_respuesta: palabras } : null;
    })();

    const retroalimentacion_config = (() => {
      const payload = {};
      const modo = trimOrNull(fbModo);
      if (modo) payload.modo = modo;
      if (fbExplainBad) payload.explicar_incorrectas = true;
      return Object.keys(payload).length ? payload : null;
    })();

    let bloom_objetivo = null;
    let niveles_bloom = [];
    const bloomCountsSum = Object.values(bloomCounts).reduce(
      (acc, n) => acc + Math.max(0, Number(n) || 0),
      0,
    );

    if (bloomMode === "custom") {
      niveles_bloom = BLOOM_LEVELS.filter((lvl) => (Number(bloomCounts[lvl]) || 0) > 0);
      if (bloomCountsSum > 0) bloom_objetivo = { ...bloomCounts };
    } else {
      niveles_bloom = BLOOM_LEVELS.filter((lvl) => enabledBloom[lvl]);
      if (niveles_bloom.length && totalConfig > 0) {
        bloom_objetivo = distributeCounts(totalConfig, niveles_bloom, {
          recordar: 0,
          comprender: 0,
          aplicar: 0,
          analizar: 0,
          evaluar: 0,
          crear: 0,
        });
      }
    }

    if (!niveles_bloom.length) {
      setErr("Selecciona al menos un nivel de Bloom o define cantidades manuales.");
      return;
    }

    if (bloomMode === "custom" && bloomCountsSum && bloomCountsSum !== totalConfig) {
      setErr(`La distribución manual de Bloom suma ${bloomCountsSum}, pero el total enviado será ${totalConfig}. Ajusta los niveles para que coincida.`);
      return;
    }

    const invalidRangeResource = selectedResources.find((entry) => {
      if (!entry?.pageRangeEnabled || !canUseResourceRange(entry)) return false;
      const from = Number.parseInt(String(entry.pageFrom || "").trim(), 10);
      const to = Number.parseInt(String(entry.pageTo || "").trim(), 10);
      const maxPages = Number.isInteger(entry?.pageCount)
        ? entry.pageCount
        : null;
      return (
        !Number.isInteger(from) ||
        !Number.isInteger(to) ||
        from < 1 ||
        to <= from ||
        (maxPages !== null && to > maxPages)
      );
    });
    if (invalidRangeResource) {
      setErr(
        `Rango de páginas inválido en: ${invalidRangeResource?.titulo || invalidRangeResource?.nombre_original || invalidRangeResource?.nombre_archivo || "documento seleccionado"}.`,
      );
      return;
    }

    const resource_page_ranges = selectedResources
      .filter(
        (entry) =>
          entry?.id != null &&
          entry?.pageRangeEnabled &&
          canUseResourceRange(entry),
      )
      .map((entry) => ({
        resource_id: entry.id,
        enabled: true,
        from: Number.parseInt(String(entry.pageFrom).trim(), 10),
        to: Number.parseInt(String(entry.pageTo).trim(), 10),
      }));

    const payload = {
      ia: true,
      count: totalConfig,
      tipos: {
        unica: clampInt(tiposFinal.unica),
        multiple: clampInt(tiposFinal.multiple),
        vf: clampInt(tiposFinal.vf),
        completar: clampInt(tiposFinal.completar),
      },
      niveles_bloom,
      ...(bloom_objetivo ? { bloom_objetivo } : {}),
      instrucciones_adicionales: (prompt || "").trim(),
      usar_bert: !!useBert,
      force_ocr: !!useOCR,
      ...(dificultad_objetivo ? { dificultad_objetivo } : {}),

      // extras que tu backend ya soporta (como uploader.html)
      tolerancia_dificultad: clampInt(tolerancia, 0, 10),
      max_reparaciones: clampInt(maxReparaciones, 0, 3),

      ...(estiloFinal ? { estilo: estiloFinal } : {}),
      ...(estilos?.length ? { estilos } : {}),
      ...(estilo_objetivo ? { estilo_objetivo } : {}),
      ...(contextoCaso ? { contexto_caso: true } : {}),
      ...(contextoCaso && trimOrNull(longitudContexto)
        ? { longitud_contexto: trimOrNull(longitudContexto) }
        : {}),

      incluir_evidencia: !!incluirEvidencia,


      ...(opciones_config ? { opciones_config } : {}),
      ...(completar_config ? { completar_config } : {}),
      ...(retroalimentacion_config ? { retroalimentacion_config } : {}),
      ...(selectedResources?.length
        ? { recursos_ids: selectedResources.map((r) => r.id) }
        : {}),
      ...(resource_page_ranges.length ? { resource_page_ranges } : {}),
      ...(tempFilesMode && temporaryFiles?.length
        ? { files: temporaryFiles, _temporary_files: true }
        : {}),
    };

    const generationController = new AbortController();
    const runId = generationRunIdRef.current + 1;
    generationRunIdRef.current = runId;
    generationAbortRef.current = generationController;

    try {
      setLoading(true);
      setProcCancelling(false);
      setProcCancelled(false);
      setGeneratedQuestions([]);
      setReviewOpen(false);
      if (!submitFn)
        throw new Error("No hay handler para generar (onSubmit/onGenerate)");

      // 1) Modal de proceso
      openProcess({ title: "Procesando…", subtitle: "Iniciando…" });

      // 2) Ejecutar generación (backend)
      const res = await submitFn({
        ...payload,
        _signal: generationController.signal,
      });

      if (generationController.signal.aborted || generationRunIdRef.current !== runId) {
        finishProcess(false, "Generación cancelada por el usuario.", "cancelled");
        return;
      }

      const qs = extractQuestionsFromResponse(res);

      if (generationController.signal.aborted || generationRunIdRef.current !== runId) {
        finishProcess(false, "Generación cancelada por el usuario.", "cancelled");
        return;
      }

      setGeneratedQuestions(qs);

      // 3) Finalizar proceso
      finishProcess(
        true,
        qs.length
          ? `Listo ✅ Se generaron ${qs.length} preguntas.`
          : "Listo ✅",
      );

      // 4) Si vienen preguntas, abrir revisión; si no, cerramos como antes
      if (qs.length) {
        setReviewOpen(true);
      } else {
        // fallback: cerrar el modal principal
        onClose?.();
      }
    } catch (e) {
      const isAbort = e?.name === "AbortError" || String(e?.message || "").toLowerCase().includes("cancel");
      const msg = isAbort
        ? "Generación cancelada por el usuario."
        : e?.message || "No se pudo generar con IA.";
      setErr(msg);
      finishProcess(false, msg, isAbort ? "cancelled" : "error");
    } finally {
      if (generationRunIdRef.current === runId) {
        setLoading(false);
        setProcCancelling(false);
      }
      if (generationAbortRef.current === generationController) {
        generationAbortRef.current = null;
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="aiq-modal-overlay" role="dialog" aria-modal="true">
        <div className="aiq-modal">
          <div className="aiq-modal-header">
            <h3 className="aiq-title">
              <span className="aiq-icon">
                <Sparkles size={18} />
              </span>
              Generar preguntas con IA
            </h3>
            <button className="aiq-close" onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>

          <div className="aiq-body">
            <div className="aiq-grid">
              {/* ===== Columna 1 ===== */}
              <div className="aiq-card">
                {/* ===== Recursos (API_IA) ===== */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <div>
                    <h4 style={{ margin: 0, fontSize: 15} } >Recursos </h4>
                    <p className="aiq-muted" style={{ margin: "6px 0 0" }}>
                      {tempFilesMode
                        ? "Puedes subir PDFs/PPT/DOCX/TXT solo para esta generación. No se guardarán como recursos del curso."
                        : "Puedes enviar PDFs/PPT/DOCX/TXT para que la IA extraiga contenido y genere preguntas más precisas."}
                    </p>
                    {resourcesParentId &&
                      parentId &&
                      String(resourcesParentId) !== String(parentId) && (
                        <p
                          className="aiq-muted"
                          style={{ margin: "6px 0 0", fontWeight: 800 }}
                        >
                          Nota: el material se guardará en el{" "}
                          <b>módulo padre</b> de esta tarea.
                        </p>
                      )}
                  </div>
                  <div
                    className="aiq-actions"
                    style={{ alignItems: "center", flexWrap: "wrap" }}
                  >
                    {!tempFilesMode && (
                      <button
                        type="button"
                        className="aiq-btn secondary"
                        onClick={openResourcePicker}
                        disabled={loading || uploadingExternal}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <FolderOpen size={16} /> Seleccionar desde recursos
                          {selectedResources?.length
                            ? ` (${selectedResources.length})`
                            : ""}
                        </span>
                      </button>
                    )}

                    <button
                      type="button"
                      className="aiq-btn secondary"
                      onClick={triggerExternalUpload}
                      disabled={loading || uploadingExternal}
                      title={tempFilesMode ? "Subir archivos temporales solo para esta generación" : "Subir archivos externos y guardarlos como recursos"}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {uploadingExternal ? (
                          <Loader2 size={16} className="aiq-spin" />
                        ) : (
                          <FileUp size={16} />
                        )}
                        {uploadingExternal
                          ? "Subiendo..."
                          : tempFilesMode
                            ? "Subir material"
                            : "Subir material"}
                      </span>
                    </button>

                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      onChange={handleExternalFilesSelected}
                    />
                  </div>
                </div>

                {uploadExternalErr && (
                  <p
                    className="aiq-muted"
                    style={{
                      margin: "10px 0 0",
                      color: "#b91c1c",
                      fontWeight: 700,
                    }}
                  >
                    <span style={{ marginRight: 6 }}>⚠️</span>
                    {uploadExternalErr}
                  </p>
                )}

                {selectedResources?.length > 0 && (
                  <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                    {selectedResources.map((r) => {
                      const maxPages = getResourcePageCount(r);
                      const pageApprox = getResourcePageCountApprox(r);
                      const canRange = canUseResourceRange(r);
                      const currentFrom = Number.parseInt(
                        String(r.pageFrom || "").trim(),
                        10,
                      );
                      const minTo =
                        Number.isInteger(currentFrom) && currentFrom >= 1
                          ? currentFrom + 1
                          : 2;
                      return (
                        <div
                          key={r.id}
                          style={{
                            padding: "10px 12px",
                            border: "1px solid rgba(15,23,42,.10)",
                            borderRadius: 12,
                            background: "rgba(15,23,42,.02)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              <span
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 10,
                                  display: "grid",
                                  placeItems: "center",
                                  background: "rgba(59,130,246,.12)",
                                  color: "#1d4ed8",
                                  flex: "0 0 auto",
                                }}
                              >
                                <FileText size={16} />
                              </span>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                  style={{
                                    fontWeight: 800,
                                    fontSize: 12,
                                    color: "#0f172a",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {r.titulo ||
                                    r.nombre_original ||
                                    r.nombre_archivo ||
                                    `Recurso #${r.id}`}
                                </div>
                                <div
                                  className="aiq-muted"
                                  style={{ marginTop: 2 }}
                                >
                                  {formatSelectedResourceMeta(r)}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="aiq-btn secondary"
                              onClick={() => removeSelectedResource(r.id)}
                              title="Quitar"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>

                          {canRange && (
                            <div className="aiq-range">
                              <label className="aiq-range-toggle">
                                <input
                                  type="checkbox"
                                  checked={!!r.pageRangeEnabled}
                                  onChange={(e) =>
                                    updateSelectedResource(r.id, (prev) => {
                                      const checked = !!e.target.checked;
                                      if (!checked) {
                                        return {
                                          ...prev,
                                          pageRangeEnabled: false,
                                          pageFrom: "",
                                          pageTo: "",
                                        };
                                      }
                                      const total = getResourcePageCount(prev);
                                      return {
                                        ...prev,
                                        pageRangeEnabled: true,
                                        pageFrom: total && total > 1 ? "1" : "",
                                        pageTo:
                                          total && total > 1
                                            ? String(total)
                                            : "",
                                      };
                                    })
                                  }
                                />
                                Elegir rango de páginas
                              </label>

                              {!!r.pageRangeEnabled && (
                                <div className="aiq-range-fields">
                                  <div className="aiq-field">
                                    <label>De</label>
                                    <input
                                      type="number"
                                      min={1}
                                      max={
                                        maxPages && maxPages > 1
                                          ? maxPages - 1
                                          : undefined
                                      }
                                      value={r.pageFrom || ""}
                                      onChange={(e) =>
                                        updateSelectedResource(r.id, {
                                          pageFrom: e.target.value,
                                        })
                                      }
                                      placeholder="1"
                                    />
                                  </div>
                                  <div className="aiq-field">
                                    <label>Hasta</label>
                                    <input
                                      type="number"
                                      min={minTo}
                                      max={maxPages || undefined}
                                      value={r.pageTo || ""}
                                      onChange={(e) =>
                                        updateSelectedResource(r.id, {
                                          pageTo: e.target.value,
                                        })
                                      }
                                      placeholder={
                                        maxPages ? String(maxPages) : "2"
                                      }
                                    />
                                  </div>
                                </div>
                              )}

                              <div
                                className="aiq-muted"
                                style={{ marginTop: 6 }}
                              >
                                {Number.isInteger(maxPages)
                                  ? `Documento con ${maxPages} ${getResourceExt(r) === "pptx" ? "diapositivas" : "páginas"}${pageApprox ? " aprox." : ""}.`
                                  : "Puedes definir el rango manualmente si deseas limitar la extracción."}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}


                {tempFilesMode && temporaryFiles?.length > 0 && (
                  <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                    {temporaryFiles.map((file, idx) => (
                      <div
                        key={`${file.name}-${file.size}-${file.lastModified}-${idx}`}
                        style={{
                          padding: "10px 12px",
                          border: "1px solid rgba(15,23,42,.10)",
                          borderRadius: 12,
                          background: "rgba(15,23,42,.02)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                          <span
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 10,
                              display: "grid",
                              placeItems: "center",
                              background: "rgba(59,130,246,.12)",
                              color: "#1d4ed8",
                              flex: "0 0 auto",
                            }}
                          >
                            <FileText size={16} />
                          </span>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 800,
                                fontSize: 12,
                                color: "#0f172a",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {file.name}
                            </div>
                            <div className="aiq-muted" style={{ marginTop: 2 }}>
                              {`${(Number(file.size || 0) / 1024).toFixed(2)} KB • material temporal`}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="aiq-btn secondary"
                          onClick={() => removeTemporaryFile(idx)}
                          title="Quitar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="aiq-divider" />

                <section className="aiq-config-shell">
                  <div className="aiq-config-titlebar">
                    <div>
                      <h4 style={{ margin: 0, fontSize: 15 }}>Configuración de Preguntas</h4>
                      <p className="aiq-muted" style={{ margin: "6px 0 0" }}>
                        Configura el banco de forma simple. Si no tocas Avanzado, el sistema elige valores razonables.
                      </p>
                    </div>
                    <div className="aiq-total-bar" aria-label="Cantidad total de preguntas">
                      <span className="aiq-total-label">Cantidad</span>
                      <div className="aiq-total-presets" role="group" aria-label="Cantidades predeterminadas">
                        {totalPresets.map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            className={`aiq-total-btn ${count === preset ? "is-active" : ""}`}
                            onClick={() => setQuestionTotal(preset)}
                          >
                            {preset}
                          </button>
                        ))}
                      </div>
                      <span className="aiq-total-label">Otro</span>
                      <input
                        className="aiq-total-other"
                        type="number"
                        min={1}
                        max={50}
                        value={count}
                        onChange={(e) => setQuestionTotal(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="aiq-main-reflow">
                    <div className="aiq-option-card">
                      <div className="aiq-option-heading">
                        <div className="aiq-option-title">Tipo de Preguntas</div>
                        <div className="aiq-mode-switch">
                          <button type="button" className={`aiq-mode-btn ${useAutoTipos ? "is-active" : ""}`} onClick={() => switchTypeMode("auto")}>Automático</button>
                          <button type="button" className={`aiq-mode-btn ${!useAutoTipos ? "is-active" : ""}`} onClick={() => switchTypeMode("manual")}>Manual</button>
                        </div>
                      </div>
                      <div className="aiq-mode-grid aiq-type-grid">
                        {[
                          ["unica", "Única", useUnica, setUseUnica],
                          ["multiple", "Múltiple", useMultiple, setUseMultiple],
                          ["vf", "V/F", useVF, setUseVF],
                          ["completar", "Completar", useCompletar, setUseCompletar],
                        ].map(([key, label, checked, setter]) => (
                          <div key={key} className={`aiq-count-item ${useAutoTipos && checked ? "is-auto-selected" : ""}`}>
                            <label className="aiq-count-label">
                              <input
                                type="checkbox"
                                checked={!!checked}
                                disabled={!useAutoTipos}
                                onChange={(e) => setter(e.target.checked)}
                              />
                              <span>{label}</span>
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={50}
                              value={typeCountValues[key] || 0}
                              disabled={useAutoTipos}
                              onChange={(e) => updateManualTypeCount(key, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="aiq-config-hint" style={{ color: tiposHint.color }}>{tiposHint.text}</p>
                    </div>

                    <div className="aiq-option-card">
                      <div className="aiq-option-heading">
                        <div className="aiq-option-title">Enfoque</div>
                        <div className="aiq-mode-switch">
                          <button type="button" className={`aiq-mode-btn ${styleMode === "auto" ? "is-active" : ""}`} onClick={() => switchStyleMode("auto")}>Automático</button>
                          <button type="button" className={`aiq-mode-btn ${styleMode === "custom" ? "is-active" : ""}`} onClick={() => switchStyleMode("custom")}>Manual</button>
                        </div>
                      </div>
                      <div className="aiq-mode-grid aiq-style-grid">
                        {STYLE_KEYS.map((key) => (
                          <div key={key} className={`aiq-count-item ${styleMode === "auto" && enabledStyle[key] ? "is-auto-selected" : ""}`}>
                            <label className="aiq-count-label">
                              <input
                                type="checkbox"
                                checked={!!enabledStyle[key]}
                                disabled={styleMode !== "auto"}
                                onChange={() => toggleEnabledMap(setEnabledStyle, enabledStyle, key)}
                              />
                              <span>{STYLE_LABELS[key]}</span>
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={50}
                              value={displayedStyleCounts[key] || 0}
                              disabled={styleMode === "auto"}
                              onChange={(e) => updateStyleCount(key, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="aiq-config-hint" style={{ color: styleHint.color }}>{styleHint.text}</p>
                    </div>

                    <div className="aiq-option-card">
                      <div className="aiq-option-heading">
                        <div className="aiq-option-title">Dificultad</div>
                        <div className="aiq-mode-switch">
                          <button type="button" className={`aiq-mode-btn ${diffMode === "auto" ? "is-active" : ""}`} onClick={() => switchDiffMode("auto")}>Automático</button>
                          <button type="button" className={`aiq-mode-btn ${diffMode === "custom" ? "is-active" : ""}`} onClick={() => switchDiffMode("custom")}>Manual</button>
                        </div>
                      </div>
                      <div className="aiq-mode-grid aiq-diff-grid">
                        {[
                          ["facil", "Fáciles"],
                          ["media", "Medias"],
                          ["dificil", "Difíciles"],
                        ].map(([key, label]) => (
                          <div key={key} className={`aiq-count-item ${diffMode === "auto" && enabledDiff[key] ? "is-auto-selected" : ""}`}>
                            <label className="aiq-count-label">
                              <input
                                type="checkbox"
                                checked={!!enabledDiff[key]}
                                disabled={diffMode !== "auto"}
                                onChange={() => toggleEnabledMap(setEnabledDiff, enabledDiff, key)}
                              />
                              <span>{label}</span>
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={50}
                              value={displayedDiffCounts[key] || 0}
                              disabled={diffMode === "auto"}
                              onChange={(e) => {
                                const next = clampGroupToTotal(
                                  { facil: diffEasy, media: diffMed, dificil: diffHard },
                                  key,
                                  e.target.value,
                                  totalForConfig,
                                );
                                setDiffEasy(next.facil);
                                setDiffMed(next.media);
                                setDiffHard(next.dificil);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="aiq-config-hint" style={{ color: diffHint.color }}>{diffHint.text}</p>
                    </div>
                  </div>

                  <div className="aiq-option-card" style={{ marginTop: 10 }}>
                    <div className="aiq-option-heading">
                      <div className="aiq-option-title">Niveles de Bloom</div>
                      <div className="aiq-mode-switch">
                        <button type="button" className={`aiq-mode-btn ${bloomMode === "auto" ? "is-active" : ""}`} onClick={() => switchBloomMode("auto")}>Automático</button>
                        <button type="button" className={`aiq-mode-btn ${bloomMode === "custom" ? "is-active" : ""}`} onClick={() => switchBloomMode("custom")}>Manual</button>
                      </div>
                    </div>
                    <div className="aiq-mode-grid aiq-bloom-count-grid">
                      {BLOOM_LEVELS.map((lvl) => (
                        <div key={lvl} className={`aiq-count-item ${bloomMode === "auto" && enabledBloom[lvl] ? "is-auto-selected" : ""}`}>
                          <label className="aiq-count-label">
                            <input
                              type="checkbox"
                              checked={!!enabledBloom[lvl]}
                              disabled={bloomMode !== "auto"}
                              onChange={() => toggleEnabledMap(setEnabledBloom, enabledBloom, lvl)}
                            />
                            <span>{lvl.charAt(0).toUpperCase() + lvl.slice(1)}</span>
                          </label>
                          <input
                            type="number"
                            min={0}
                            max={50}
                            value={displayedBloomCounts[lvl] || 0}
                            disabled={bloomMode === "auto"}
                            onChange={(e) => updateBloomCount(lvl, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                    <p className="aiq-config-hint" style={{ color: bloomHint.color }}>{bloomHint.text}</p>
                  </div>
                </section>

                <details className="aiq-details" style={{ marginTop: 12 }}>
                  <summary><span className="aiq-help"><SlidersHorizontal size={16} /> Configuración avanzada</span><span className="aiq-muted">abrir/cerrar</span></summary>
                  <div className="aiq-divider" />
                  <div className="aiq-advanced-grid">
                    <div className="aiq-advanced-card">
                      <h4>Procesamiento del documento</h4>
                      <p className="aiq-muted">Opciones para extraer texto y reforzar la calidad del banco.</p>
                      <div className="aiq-chips" style={{ marginTop: 10 }}>
                        <label className="aiq-chip" style={{ cursor: "pointer" }}>
                          <input type="checkbox" checked={useOCR} onChange={(e) => setUseOCR(e.target.checked)} />
                          Extraer texto de imagenes dentro del documento (OCR)
                        </label>
                        <label className="aiq-chip" style={{ cursor: "pointer" }}>
                          <input type="checkbox" checked={useBert} onChange={(e) => setUseBert(e.target.checked)} />
                          Maximizar calidad de preguntas (BERT)
                        </label>
                      </div>
                      <div className="aiq-muted" style={{ marginTop: 8 }}>
                        El OCR ayuda con PDFs escaneados o imágenes con texto. BERT refuerza la calidad de las preguntas.
                      </div>
                      <div className="aiq-note">
                        Activar estas opciones podría aumentar ligeramente el tiempo de procesamiento
                      </div>
                    </div>

                    <div className="aiq-advanced-card">
                      <h4>Ajustes para Selección y completar</h4>
                      <p className="aiq-muted">Ajustes prácticos para preguntas de selección y completar.</p>
                      <div className="aiq-row" style={{ marginTop: 12 }}>
                        <div className="aiq-field"><label>Opciones por pregunta (selección)</label><input type="number" min={4} max={8} value={numOpciones} onChange={(e) => setNumOpciones(e.target.value)} placeholder="4" disabled={tiposFinal.multiple <= 0 && tiposFinal.unica <= 0} /></div>
                        <div className="aiq-field"><label>En multiple, cuántas correctas</label><select value={cantidadCorrectas} onChange={(e) => setCantidadCorrectas(e.target.value)} disabled={tiposFinal.multiple <= 0}><option value="">(auto)</option><option value="2">2 correctas</option><option value="3">3 correctas</option><option value="4">4 correctas</option></select></div>
                      </div>
                      <div className="aiq-row" style={{ marginTop: 10 }}>
                        <div className="aiq-field"><label>Distractores (opciones incorrectas)</label><select value={distractores} onChange={(e) => setDistractores(e.target.value)}><option value="">(auto)</option><option value="plausibles">plausibles</option><option value="errores_comunes">errores_comunes</option><option value="conceptos_cercanos">conceptos_cercanos</option></select></div>
                        <div className="aiq-field"><label>Completar: palabras a completar</label><select value={palabrasRespuesta} onChange={(e) => setPalabrasRespuesta(e.target.value)} disabled={tiposFinal.completar <= 0}><option value="">(automático)</option><option value="2">2 palabras</option><option value="3">3 palabras</option><option value="4">4 palabras</option></select></div>
                      </div>
                      <label className="aiq-chip" style={{ display: "none", marginTop: 10 }}><input type="checkbox" checked={evitarAbsurdos} onChange={(e) => setEvitarAbsurdos(e.target.checked)} /> Evitar distractores absurdos</label>
                    </div>

                    <div className="aiq-advanced-card">
                      <h4>Retroalimentación, evidencia y contexto</h4>
                      <p className="aiq-muted"><b>Modo</b> controla la profundidad de la explicación. <b>Evidencia</b> añade fragmentos del texto para trazabilidad.</p>
                      <div className="aiq-field" style={{ marginTop: 12 }}><label>Modo feedback / pista</label><select value={fbModo} onChange={(e) => setFbModo(e.target.value)}><option value="">(auto)</option><option value="breve">breve (1-2 frases)</option><option value="explicativa">explicativa</option><option value="paso_a_paso">paso_a_paso</option></select></div>
                      <div className="aiq-chips" style={{ marginTop: 10 }}>
                        <label className="aiq-chip"><input type="checkbox" checked={fbExplainBad} onChange={(e) => setFbExplainBad(e.target.checked)} /> Explicar incorrectas</label>
                        <label className="aiq-chip"><input type="checkbox" checked={incluirEvidencia} onChange={(e) => setIncluirEvidencia(e.target.checked)} /> Incluir evidencia del texto</label>
                      </div>
                      <div className="aiq-inline-check-row">
                        <label className="aiq-chip" style={{ justifyContent: "flex-start" }}>
                          <input type="checkbox" checked={contextoCaso} onChange={(e) => {
                            const checked = e.target.checked;
                            setContextoCaso(checked);
                            if (!checked) setLongitudContexto("");
                          }} />
                          Incluir contexto/caso antes del enunciado
                        </label>
                        {contextoCaso && (
                          <div className="aiq-field">
                            <label>Longitud</label>
                            <select value={longitudContexto} onChange={(e) => setLongitudContexto(e.target.value)}>
                              <option value="">(auto)</option><option value="corto">corto</option><option value="medio">medio</option><option value="largo">largo</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="aiq-advanced-card" style={{ marginTop: 10 }}>
                    <h4>Instrucciones adicionales</h4>
                    <div className="aiq-field"><textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={`Ej:
Enfócate solo en conceptos del capítulo 2.
Evita biografías y fechas.
Usa lenguaje claro para estudiantes de primer semestre.`} /></div>
                  </div>
                </details>
              </div>
            </div>
          </div>

          {showResourcePicker && !tempFilesMode && (
            <div
              className="aiq-modal-overlay"
              style={{ zIndex: 10001 }}
              role="dialog"
              aria-modal="true"
            >
              <div
                className="aiq-modal"
                style={{
                  width: "min(820px, 100%)",
                  maxHeight: "min(84vh, 920px)",
                }}
              >
                <div className="aiq-modal-header">
                  <h3 className="aiq-title">
                    <span
                      className="aiq-icon"
                      style={{
                        background: "linear-gradient(135deg,#f59e0b,#22c55e)",
                      }}
                    >
                      <FolderOpen size={18} />
                    </span>
                    Seleccionar recursos
                  </h3>
                  <button
                    className="aiq-close"
                    onClick={() => setShowResourcePicker(false)}
                    aria-label="Cerrar"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="aiq-body">
                  <div className="aiq-card">
                    <p className="aiq-muted" style={{ marginTop: 0 }}>
                      Elige los recursos que se enviarán a la API_IA para
                      extraer el contenido (PDF/PPT/DOCX/TXT recomendados).
                    </p>

                    {Array.isArray(resourceParentOptions) &&
                      resourceParentOptions.length > 0 && (
                        <>
                          <div
                            style={{
                              marginTop: 10,
                              padding: 12,
                              borderRadius: 14,
                              border: "1px solid rgba(15,23,42,.10)",
                              background: "rgba(15,23,42,.02)",
                            }}
                          >
                            <div className="aiq-field" style={{ margin: 0 }}>
                              <label>{resourceParentLabel}</label>
                              <select
                                value={selectedResourceParentId ?? ""}
                                onChange={async (e) => {
                                  const raw = e.target.value;
                                  const parsed =
                                    raw === "" ? null : Number(raw);
                                  const nextParentId = Number.isFinite(parsed)
                                    ? parsed
                                    : null;

                                  setSelectedResourceParentId(nextParentId);
                                  setResources([]);
                                  setResourcesSearch("");
                                  setResourcesErr("");

                                  if (!nextParentId) return;

                                  setResourcesLoading(true);
                                  try {
                                    const res =
                                      await authService.authenticatedRequest(
                                        `/api/admin/estructura/${encodeURIComponent(nextParentId)}/resources`,
                                        { method: "GET" },
                                      );
                                    const json = await res
                                      .json()
                                      .catch(() => ({}));
                                    if (!res.ok) {
                                      throw new Error(
                                        json?.message ||
                                        json?.error ||
                                        `No se pudieron cargar recursos (HTTP ${res.status})`,
                                      );
                                    }
                                    const rawList = Array.isArray(json?.resources)
                                      ? json.resources
                                      : Array.isArray(json)
                                        ? json
                                        : [];
                                    setResources(
                                      rawList.map((item) =>
                                        normalizeSelectedResource(item),
                                      ),
                                    );
                                  } catch (errLoad) {
                                    setResourcesErr(
                                      errLoad?.message ||
                                      "Error cargando recursos",
                                    );
                                    setResources([]);
                                  } finally {
                                    setResourcesLoading(false);
                                  }
                                }}
                                disabled={
                                  loading ||
                                  uploadingExternal ||
                                  resourcesLoading
                                }
                              >
                                {resourceParentOptions.map((opt) => (
                                  <option key={opt.id} value={opt.id}>
                                    {opt.label ||
                                      opt.nombre ||
                                      `Apartado #${opt.id}`}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="aiq-muted" style={{ marginTop: 8 }}>
                              Selecciona primero el módulo y luego el recurso
                              que quieras usar.
                            </div>
                          </div>

                          <div className="aiq-divider" />
                        </>
                      )}

                    {!resourcesEstructuraId && (
                      <div
                        style={{
                          padding: 10,
                          borderRadius: 12,
                          border: "1px solid rgba(239,68,68,.25)",
                          background: "rgba(239,68,68,.06)",
                          color: "#991b1b",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        No se detectó el contexto para recursos. Selecciona un
                        apartado válido para listar o subir material.
                      </div>
                    )}

                    {!!resourcesErr && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: 10,
                          borderRadius: 12,
                          border: "1px solid rgba(239,68,68,.25)",
                          background: "rgba(239,68,68,.06)",
                          color: "#991b1b",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {resourcesErr}
                      </div>
                    )}

                    <div className="aiq-row" style={{ marginTop: 10 }}>
                      <div className="aiq-field">
                        <label>Buscar</label>
                        <input
                          value={resourcesSearch}
                          onChange={(e) => setResourcesSearch(e.target.value)}
                          placeholder="Ej: unidad 1, pdf, diapositivas…"
                        />
                      </div>
                      <div className="aiq-field">
                        <label>Seleccionados</label>
                        <input
                          value={String(selectedResources?.length || 0)}
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="aiq-divider" />

                    {resourcesLoading ? (
                      <p className="aiq-muted">Cargando recursos…</p>
                    ) : (
                      <div style={{ display: "grid", gap: 8 }}>
                        {(resources || [])
                          .filter((r) => {
                            const q = (resourcesSearch || "")
                              .trim()
                              .toLowerCase();
                            if (!q) return true;
                            const t = String(r.titulo || "").toLowerCase();
                            const o = String(
                              r.nombre_original || "",
                            ).toLowerCase();
                            const a = String(
                              r.nombre_archivo || "",
                            ).toLowerCase();
                            return (
                              t.includes(q) || o.includes(q) || a.includes(q)
                            );
                          })
                          .map((r) => {
                            const checked = selectedResources.some(
                              (x) => x.id === r.id,
                            );
                            return (
                              <button
                                type="button"
                                key={r.id}
                                onClick={() => toggleResourceSelection(r)}
                                style={{
                                  textAlign: "left",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 10,
                                  padding: "10px 12px",
                                  borderRadius: 14,
                                  border: checked
                                    ? "1px solid rgba(34,197,94,.50)"
                                    : "1px solid rgba(15,23,42,.10)",
                                  background: checked
                                    ? "rgba(34,197,94,.08)"
                                    : "#fff",
                                  cursor: "pointer",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    minWidth: 0,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    readOnly
                                    checked={checked}
                                  />
                                  <div style={{ minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontWeight: 900,
                                        fontSize: 12,
                                        color: "#0f172a",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {r.titulo ||
                                        r.nombre_original ||
                                        r.nombre_archivo ||
                                        `Recurso #${r.id}`}
                                    </div>
                                    <div
                                      className="aiq-muted"
                                      style={{ marginTop: 2 }}
                                    >
                                      {r.size_formatted ||
                                        (r.tamaño_archivo
                                          ? `${Math.round(Number(r.tamaño_archivo) / 1024)} KB`
                                          : "")}
                                      {r.tipo_contenido
                                        ? ` • ${r.tipo_contenido}`
                                        : ""}
                                    </div>
                                  </div>
                                </div>
                                <span
                                  className="aiq-muted"
                                  style={{ fontWeight: 800 }}
                                >
                                  {checked ? "Listo" : "Elegir"}
                                </span>
                              </button>
                            );
                          })}

                        {(!resources || resources.length === 0) && (
                          <p className="aiq-muted" style={{ margin: 0 }}>
                            No hay recursos en este apartado.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="aiq-footer">
                  <p className="aiq-muted" style={{ margin: 0 }}>
                    <strong>{selectedResources.length}</strong> recurso(s)
                    seleccionado(s)
                  </p>
                  <div className="aiq-actions">
                    <button
                      className="aiq-btn secondary"
                      onClick={() => setShowResourcePicker(false)}
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="aiq-footer">
            <p className="aiq-err">{err}</p>
            <div className="aiq-actions">
              <button
                className="aiq-btn secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                className="aiq-btn primary"
                onClick={handleGenerate}
                disabled={loading || !hasGenerationMaterial}
                title={!hasGenerationMaterial ? (tempFilesMode ? "Sube al menos un documento temporal para generar." : "Selecciona o sube al menos un documento para generar.") : undefined}
              >
                {loading ? "Generando..." : "Generar"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Modal de proceso ===== */}
      {procOpen && (
        <div
          className="aiq-process-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aiqProcTitle"
        >
          <div className="aiq-process">
            <div className="aiq-process-h">
              <div className="aiq-process-title" id="aiqProcTitle">
                {procTitle}
              </div>
              <button
                type="button"
                className="aiq-process-close"
                onClick={() => procDone && setProcOpen(false)}
                disabled={!procDone}
                aria-label="Cerrar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="aiq-process-b">
              <div className="aiq-process-sub">{procSubtitle}</div>
              <div className="aiq-progress">
                <div
                  style={{
                    width: `${Math.max(2, Math.round(((procStates?.filter((s) => s === "done")?.length || 0) / Math.max(1, procSteps?.length || 1)) * 100))}%`,
                  }}
                />
              </div>
              <ul className="aiq-steps">
                {(procSteps || []).map((t, i) => (
                  <li
                    key={i}
                    className="aiq-step"
                    data-state={procStates?.[i] || "pending"}
                  >
                    <div className="aiq-step-dot" />
                    <div className="aiq-step-text">
                      <div className="aiq-step-title">{t}</div>
                      <div className="aiq-step-state">
                        {procStates?.[i] === "active"
                          ? "En proceso…"
                          : procStates?.[i] === "done"
                            ? "Completado"
                            : procStates?.[i] === "cancelled"
                              ? "Cancelado"
                              : procStates?.[i] === "error"
                                ? "Error"
                                : "En cola"}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="aiq-process-foot">
                {procDone ? (
                  <>
                    {!procCancelled && generatedQuestions?.length ? (
                      <button
                        type="button"
                        className="aiq-btn primary"
                        onClick={() => {
                          setProcOpen(false);
                          setReviewOpen(true);
                        }}
                      >
                        Ver preguntas
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="aiq-btn secondary"
                        onClick={() => {
                          setProcOpen(false);
                          if (!procCancelled) onClose?.();
                        }}
                      >
                        Cerrar
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className="aiq-btn secondary"
                    onClick={handleCancelGeneration}
                    disabled={procCancelling}
                  >
                    {procCancelling ? "Cancelando…" : "Cancelar generación"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Modal de preguntas generadas (revisión) ===== */}
      {reviewOpen && (
        <div
          className="aiq-process-overlay aiq-review-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aiqReviewTitle"
        >
          <div className="aiq-review">
            <div className="aiq-process-h">
              <div className="aiq-process-title" id="aiqReviewTitle">
                Preguntas generadas ({generatedQuestions?.length || 0})
              </div>
              {/* <button
                type="button"
                className="aiq-process-close"
                onClick={() => {
                  setReviewOpen(false);
                  onClose?.();
                }}
                aria-label="Cerrar"
              >
                <X size={18} />
              </button> */}
            </div>

            <div className="aiq-review-list">
              <ul className="aiq-bank-list">
                {(generatedQuestions || []).map((q, idx) => {
                  const id =
                    q?.id ?? q?.question_id ?? q?.estructura_pregunta_id ?? idx;

                  // Normalización de campos (compatibles con uploader.html + backend)
                  const tipoRaw = q?.tipo_pregunta ?? q?.tipo ?? "";
                  const bloomRaw = q?.bloom ?? q?.nivel_bloom ?? q?.nivel ?? "";
                  // OJO: prettyTipo necesita la pregunta completa para decidir UNICA vs MULTIPLE
                  const tipo = prettyTipo(q);
                  const bloom = prettyBloom(bloomRaw);
                  const enun = q?.pregunta ?? q?.enunciado ?? q?.titulo ?? "";
                  const opcionesNorm = normalizeOpciones(q);
                  const respuestaCorrecta = formatRespuestaCorrecta(
                    q,
                    opcionesNorm,
                  );

                  const retro =
                    q?.retroalimentacion ??
                    q?.feedback ??
                    q?.explicacion ??
                    q?.explanation ??
                    null;

                  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

                  const onDelete = async () => {
                    setDeletingIds((prev) => new Set([...(prev || []), id]));
                    try {
                      if (
                        q?.id != null &&
                        typeof onDeleteQuestion === "function"
                      ) {
                        await onDeleteQuestion(q);
                      }
                      setGeneratedQuestions((prev) =>
                        (prev || []).filter((x, j) => j !== idx),
                      );
                    } catch (e) {
                      setErr(e?.message || "No se pudo eliminar la pregunta.");
                    } finally {
                      setDeletingIds((prev) => {
                        const n = new Set(prev || []);
                        n.delete(id);
                        return n;
                      });
                    }
                  };

                  const tipoUp = (tipo || tipoRaw || "pregunta").toUpperCase();
                  const bloomUp = bloom
                    ? bloom
                    : bloomRaw
                      ? bloomRaw.toUpperCase()
                      : "";

                  return (
                    <li key={id} className="aiq-bank-item">
                      <div className="aiq-bank-top">
                        <div className="aiq-bank-header">
                          [{idx + 1}] ({tipoUp}
                          {bloomUp ? ` · Bloom: ${bloomUp}` : ""})
                        </div>
                        <button
                          type="button"
                          className="aiq-btn secondary"
                          onClick={onDelete}
                          disabled={deletingIds?.has(id)}
                          title="Eliminar"
                        >
                          {deletingIds?.has(id) ? (
                            <Loader2 size={16} className="aiq-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>

                      <div className="aiq-bank-enun">
                        {enun || `Pregunta #${idx + 1}`}
                      </div>

                      {Array.isArray(opcionesNorm) &&
                        opcionesNorm.length > 0 && (
                          <ul className="aiq-clean">
                            {opcionesNorm.map((opt, k) => {
                              const correcta = !!(
                                opt?.correcta ??
                                opt?.is_correct ??
                                opt?.correct
                              );
                              const etiqueta = (
                                opt?.etiqueta ??
                                opt?.label ??
                                letters[k] ??
                                ""
                              ).toString();
                              const texto = (
                                opt?.texto ??
                                opt?.text ??
                                opt?.opcion ??
                                opt?.value ??
                                ""
                              ).toString();
                              const mark = correcta ? "✔ " : "";
                              return (
                                <li key={k}>
                                  {mark}
                                  {etiqueta}) {texto}
                                </li>
                              );
                            })}
                          </ul>
                        )}

                      {respuestaCorrecta != null &&
                        String(respuestaCorrecta).trim() !== "" && (
                          <div className="aiq-info">
                            Respuesta correcta: {String(respuestaCorrecta)}
                          </div>
                        )}

                      {retro != null && String(retro).trim() !== "" && (
                        <div className="aiq-info">{String(retro)}</div>
                      )}
                    </li>
                  );
                })}
              </ul>

              {(!generatedQuestions || generatedQuestions.length === 0) && (
                <div className="aiq-review-empty">
                  No hay preguntas para mostrar.
                </div>
              )}
            </div>

            <div className="aiq-process-foot">
              {typeof onAddToTask === "function" && (
                <button
                  type="button"
                  className="aiq-btn primary"
                  disabled={addingToTask || !(generatedQuestions?.length > 0)}
                  onClick={async () => {
                    setErr("");
                    try {
                      setAddingToTask(true);
                      await onAddToTask(generatedQuestions);
                      setReviewOpen(false);
                      onClose?.();
                    } catch (e) {
                      setErr(
                        e?.message || "No se pudieron agregar las preguntas.",
                      );
                    } finally {
                      setAddingToTask(false);
                    }
                  }}
                >
                  {addingToTask ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Loader2 size={16} className="aiq-spin" /> Agregando…
                    </span>
                  ) : (
                    addLabel
                  )}
                </button>
              )}

              <button
                type="button"
                className="aiq-btn secondary"
                onClick={() => {
                  setReviewOpen(false);
                  onClose?.();
                }}
              >
                {typeof onAddToTask === "function"
                  ? "Cerrar (sin agregar)"
                  : "Cerrar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
